import {
  type CSSProperties,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Outlet, useNavigate, useParams, useLocation, useOutletContext } from 'react-router-dom';
import { Modal } from '@helios/ui/overlay';
import { Button } from '@helios/ui/button';
import { useAuth } from '@helios/auth-firebase-web';
import {
  useBrandTokens,
  useSidebarState,
  useResizableSidebar,
  SIDEBAR_COLLAPSED_WIDTH,
  TopBar,
  NavIcon,
  ContactRow,
  ResizeGutter,
  RightDrawer,
  avatarGradient,
} from './shared';
import { usePowerSyncQuery, usePowerSyncDb } from '../adapters/powersync';
import { useFormatMoney, useRegionalSettings } from '../hooks/useFormatMoney';
import { useEnabledIdTypeLabels } from '../hooks/useRegionalIdTypes';
import { uploadToStorage } from '../lib/upload-to-storage';
import {
  qPatientById,
  qPatientContact,
  qPatientAllergies,
  qPatientEmergencyContacts,
  qLatestPatientIntakeForm,
  qIntakeFormResponse,
  qLatestPublishedIntakeFormDefinition,
  qNoOp,
  qActiveVitalDefinitions,
  qLatestPatientVitalsRecordedAt,
  qLatestPatientVitals,
  qPatientVitalsHistory,
  qPatientFieldDefinitionOverrides,
  buildPatientFormSettingsVM,
  buildPatientValidationSchema,
  type PatientDetailRow,
  type PatientContactRow,
  type PatientAllergyRow,
  type PatientEmergencyContactRow,
  type PatientIntakeFormRow,
  type IntakeFormResponseRow,
  type VitalDefinitionRow,
  type PatientVitalEntryWithDefinitionRow,
  type PatientFieldDefinitionRow,
  type IntakeFormDefinitionRow,
  qEncountersTodayByPatient,
  qPatientAlertNotes,
  qRecentTreatmentRecords,
  qTreatmentRecordsByPatient,
  qEncountersByPatient,
  qEncounterNotesPreviews,
  qInvoicesByPatient,
  type PatientEncounterRow,
  type PatientNoteRow,
  type PatientTreatmentRecordRow,
  type EncounterNotePreviewRow,
  type InvoiceRow,
  qPatientWorkspaceSettings,
  mapPatientWorkspaceSettingsToUI,
  type PatientWorkspaceSettingsRow,
} from '@helios/data-access';
import { PATIENT_CORE_FIELDS, PATIENT_SEX_OPTIONS } from '@helios/domain';
import {
  buildStepQuestionnaire,
  buildMultiSectionQuestionnaire,
  type FieldOptionsMap,
  type SectionConfig,
} from '../pages/patient/buildStepQuestionnaire';
import { VitalsDetailView } from './VitalsDetailView';
import { RecordVitalsDrawer } from './RecordVitalsDrawer';
import { SidebarVisitsPanel } from './SidebarVisitsPanel';
import { SidebarPharmacyPanel } from './SidebarPharmacyPanel';
import { SidebarPackagesPanel } from './SidebarPackagesPanel';
import { NotePreview } from '../pages/patient/encounters/timeline-previews';
import { PatientVisitNoteCTA } from './PatientVisitNoteCTA';
import {
  TimelineFormDrawer,
  TimelineInvoiceDrawer,
  TimelineFileDrawer,
} from './SidebarTimelineDrawers';
import { ensureVisitThenCreateEncounter } from '../services/encounter-service';
import { AllergiesDetailView, type AllergyItem, type MedicationInfo } from './AllergiesDetailView';
import { FamilyHistorySection } from './FamilyHistoryView';
// PRD.218 — shared patient-scoped billing aggregate (Dinero-backed).
import { usePatientBilling } from '../pages/billing/usePatientBilling';
import { PatientBillingSummaryCard } from '../pages/billing/PatientBillingSummaryCard';
import { UpdateMedicationDrawer } from './ManageAllergiesDrawer';
import { EditFamilyHistoryDrawer } from './EditFamilyHistoryDrawer';
import { ContactDetailView, type ContactData } from './ContactDetailView';
import { SendWhatsAppBookingFlowAction } from '../components/SendWhatsAppBookingFlowAction';
import { EditPatientDetailsDrawer } from './EditPatientDetailsDrawer';
import { UpdateIntakeFormDrawer } from './UpdateIntakeFormDrawer';
import type { IntakeAnswers, IntakeQuestionnaire, FieldRenderer } from '@helios/ui/intake';
import {
  ALL_COUNTRIES,
  CountryPicker,
  buildCountryOptions,
  getMobileCountryPickerConfig,
  dialCodeToIso2,
  iso2ToDialCode,
} from '@helios/ui/country-picker';
import publishedQuestionnaire from '../pages/patient/fixtures/intake-questionnaire.published.json';
import {
  usePatientTabs,
  getPatientColor,
  getInitialsShort,
  getShortLabel,
} from './PatientTabsContext';

// ─── Patient Outlet Context ───

/**
 * PRD.218 — Adapter shape consumed by the sidebar balance chip + stats row.
 * Derived from `usePatientBilling` so it stays in lockstep with the
 * Patient Overview billing card. `total_balance` is in decimal-major
 * (pesos) for the legacy compact formatters; raw cents live in the
 * underlying hook.
 */
export interface SidebarBilling {
  total_balance: number;
  unpaid_count: number;
  has_overdue: number;
}

export interface PatientOutletContext {
  collapsed: boolean;
  onToggleSidebar: () => void;
  patient: PatientDetailRow | null;
  patientName: string;
  age: number | null;
  sex: string | null;
  contactData: ContactData;
  allergyItems: AllergyItem[];
  latestVitals: LatestVitalsGrouped | null;
  alertNotes: PatientNoteRow[];
  recentTreatmentRecords: PatientTreatmentRecordRow[];
  outstandingBalance: SidebarBilling | null;
  /**
   * PRD.226 — whether the body of the patient page should also render
   * Patient Overview. Sidebar always shows it; this only gates body.
   */
  patientOverviewDisplay: 'sidebar_only' | 'sidebar_and_body';
}

export function usePatientShellContext() {
  return useOutletContext<PatientOutletContext>();
}

// ─── Vitals Types & Helpers ───

export type LatestVitalsGrouped = {
  primary: PatientVitalEntryWithDefinitionRow[];
  additional: PatientVitalEntryWithDefinitionRow[];
  recordedAt: string;
};

function formatVitalValue(row: PatientVitalEntryWithDefinitionRow): string {
  switch (row.value_type) {
    case 'number':
    case 'integer':
      return row.value_number != null ? String(row.value_number) : '--';
    case 'text':
      return row.value_text ?? '--';
    case 'date':
      return row.value_date
        ? new Date(row.value_date).toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
          })
        : '--';
    case 'boolean':
      return row.value_boolean === 1 ? 'Yes' : row.value_boolean === 0 ? 'No' : '--';
    default:
      return '--';
  }
}

function formatRecordedAt(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

// ─── Helpers ───

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

function computeAge(birthDate: string | null): number | null {
  if (!birthDate) return null;
  const d = new Date(birthDate);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - d.getFullYear();
  const monthDiff = now.getMonth() - d.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < d.getDate())) age--;
  return age;
}

function formatBalanceCompact(amount: number, symbol: string): string {
  if (amount < 1000) return `${symbol}${Math.round(amount)}`;
  if (amount < 10000) return `${symbol}${(amount / 1000).toFixed(1)}k`;
  return `${symbol}${Math.round(amount / 1000)}k`;
}

function formatBalanceFull(amount: number, symbol: string): string {
  return `${symbol}${amount.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function buildIntakeSummaryBullets(answersJson: string | null): string[] {
  if (!answersJson) return [];
  try {
    const answers = JSON.parse(answersJson) as Record<string, unknown>;
    const bullets: string[] = [];
    for (const [key, val] of Object.entries(answers)) {
      if (bullets.length >= 3) break;
      if (val === null || val === undefined || val === '') continue;
      const strVal = String(val);
      // Skip signature stroke data and long JSON blobs
      if (strVal.startsWith('{"version":') || strVal.startsWith('[{') || strVal.length > 200)
        continue;
      const label = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
      bullets.push(`${label}: ${strVal}`);
    }
    return bullets;
  } catch {
    return [];
  }
}

function toAllergyItems(rows: PatientAllergyRow[]): AllergyItem[] {
  return rows.map((r) => ({
    name: r.substance ?? 'Unknown',
    severity: (r.severity?.toLowerCase() as AllergyItem['severity']) || undefined,
    reaction: r.reaction ?? '',
  }));
}

function toContactData(
  contact: PatientContactRow | undefined,
  emergency: PatientEmergencyContactRow | undefined,
): ContactData {
  return {
    phone: contact
      ? [contact.mobile_country_code, contact.mobile_number].filter(Boolean).join(' ') ||
        contact.formal_telephone ||
        ''
      : '',
    email: contact?.email ?? '',
    street: contact?.formal_residential_address ?? '',
    city: contact?.formal_district ?? '',
    state: contact?.formal_province_state ?? '',
    zip: contact?.formal_postal_code ?? '',
    emergencyName: emergency?.name ?? '',
    emergencyRelationship: emergency?.relationship ?? '',
    emergencyPhone: emergency?.phone ?? '',
  };
}

// ─── Form Constants (match Create Patient) ───

function opt(value: string) {
  return { id: value, label: value, value };
}

const PERSONAL_INFO_OPTIONS: FieldOptionsMap = {
  doctor: ['Dr. Smith', 'Dr. Johnson', 'Dr. Williams'].map(opt),
  title: ['Mr.', 'Mrs.', 'Ms.', 'Dr.', 'Prof.'].map(opt),
  idType: ['National ID', 'Passport', 'Driver License', 'SSN', 'Other'].map(opt),
  gender: [...PATIENT_SEX_OPTIONS].map(opt),
  maritalStatus: ['Single', 'Married', 'Divorced', 'Widowed', 'Separated'].map(opt),
  referredBy: ['Walk-in', 'Doctor Referral', 'Online', 'Insurance'].map(opt),
  status: ['Active', 'Inactive', 'Archived'].map(opt),
};

const EMERGENCY_CONTACT_OPTIONS: FieldOptionsMap = {
  emergencyContactRelationship: [
    'Spouse',
    'Parent',
    'Child',
    'Sibling',
    'Relative',
    'Friend',
    'Other',
  ].map(opt),
};

const SEVERITY_OPTIONS = ['Mild', 'Moderate', 'Severe'].map(opt);

const MEDICATION_OPTIONS: FieldOptionsMap = {
  drugAllergies: ['No Known Allergies', 'Yes'].map(opt),
  foodAllergies: ['No Known Allergies', 'Yes'].map(opt),
  drugAllergySeverity: SEVERITY_OPTIONS,
  foodAllergySeverity: SEVERITY_OPTIONS,
  aboBloodGroup: ['A', 'B', 'AB', 'O'].map(opt),
  rhAntigen: ['Positive', 'Negative'].map(opt),
};

const MEDICATION_SECTIONS: SectionConfig[] = [
  {
    id: 'section-drug-allergies',
    title: 'Drug Allergies',
    fieldKeys: [
      'drugAllergies',
      'drugAllergiesSpecify',
      'drugAllergySymptoms',
      'drugAllergySeverity',
    ],
  },
  {
    id: 'section-food-allergies',
    title: 'Food Allergies',
    fieldKeys: [
      'foodAllergies',
      'foodAllergiesSpecify',
      'foodAllergySymptoms',
      'foodAllergySeverity',
    ],
  },
  {
    id: 'section-blood-type',
    title: 'Blood Type',
    fieldKeys: ['aboBloodGroup', 'rhAntigen'],
  },
  {
    id: 'section-notes-alerts',
    title: 'Notes & Alerts',
    fieldKeys: ['notes', 'alerts'],
  },
  {
    id: 'section-medication-history',
    title: 'Medication History',
    fieldKeys: ['pastMedication', 'currentMedication', 'medicalHistory'],
  },
];

// ─── Quick Nav Tab Config ───

type TabKey =
  | 'Overview'
  | 'Intake Form'
  | 'Vitals'
  | 'Allergies'
  | 'Treatment Record'
  | 'Contact'
  | 'Visits'
  // PRD.203 — Patient-wide Pharmacy. Selecting it shows a summary panel
  // in the patient sidebar body (and in the collapsed-rail right drawer)
  // with a "View All Pharmacy" link to the full /patient/:id/pharmacy
  // page.
  | 'Pharmacy'
  // PRD.261 — Patient-owned packages. Read-only right-drawer panel; always
  // visible (no workspace-settings toggle yet).
  | 'Packages';

/** Visibility map driven by Patient Workspace settings (PRD.181). */
type SidebarTabVisibility = Record<TabKey, boolean>;

const ALL_SIDEBAR_TABS_VISIBLE: SidebarTabVisibility = {
  Overview: true,
  'Intake Form': true,
  Vitals: true,
  Allergies: true,
  'Treatment Record': true,
  Contact: true,
  Visits: true,
  Pharmacy: true,
  Packages: true,
};

interface QuickNavTab {
  icon: string;
  label: TabKey;
  notification: boolean;
}

const QUICK_NAV_TABS: readonly QuickNavTab[] = [
  { icon: 'layout-grid', label: 'Overview', notification: false },
  { icon: 'heart', label: 'Vitals', notification: false },
  { icon: 'calendar', label: 'Visits', notification: false },
  { icon: 'file-text', label: 'Treatment Record', notification: false },
  // PRD.203 — Pharmacy. Uses `clipboard` because the row already spends
  // `heart` and `calendar` on Vitals/Visits.
  { icon: 'clipboard', label: 'Pharmacy', notification: false },
  // PRD.262 — Patient Packages tab (ownership + redemption).
  { icon: 'shopping-bag', label: 'Packages', notification: false },
];

// ─── Kebab Menu ───

const kebabItemStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 'var(--sp-2)',
  width: '100%',
  padding: 'var(--sp-2) var(--sp-3)',
  border: 'none',
  background: 'transparent',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  lineHeight: 'var(--line-height-sm)',
  color: 'var(--text)',
  cursor: 'pointer',
  borderRadius: 'var(--radius-sm)',
  fontFamily: 'var(--font-family)',
  textAlign: 'left' as const,
};

function KebabMenu({
  onEditPersonalInfo,
  onUpdatePhoto,
  onUpdateContact,
  onDeletePatient,
  isAdmin,
}: {
  onEditPersonalInfo: () => void;
  onUpdatePhoto: () => void;
  onUpdateContact: () => void;
  onDeletePatient: () => void;
  isAdmin: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function handleEsc(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEsc);
    };
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0 }}>
      <button
        onClick={() => setOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 28,
          height: 28,
          borderRadius: 'var(--radius-full)',
          border: '1px solid var(--border)',
          background: 'var(--bg-surface)',
          cursor: 'pointer',
          color: 'var(--text-muted)',
          padding: 0,
          fontFamily: 'var(--font-family)',
        }}
      >
        <NavIcon name="more-vertical" />
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            right: 0,
            marginTop: 'var(--sp-1)',
            minWidth: 180,
            background: 'var(--bg-surface)',
            borderRadius: 'var(--radius-lg)',
            border: '1px solid var(--border)',
            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            padding: 'var(--sp-1)',
            zIndex: 50,
          }}
        >
          {/* Edit Personal Info */}
          <button
            onClick={() => {
              setOpen(false);
              onEditPersonalInfo();
            }}
            style={kebabItemStyle}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            <NavIcon name="edit" /> Edit Personal Info
          </button>

          {/* Update Photo */}
          <button
            onClick={() => {
              setOpen(false);
              onUpdatePhoto();
            }}
            style={kebabItemStyle}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            <NavIcon name="camera" /> Update Photo
          </button>

          {/* Update Contact */}
          <button
            onClick={() => {
              setOpen(false);
              onUpdateContact();
            }}
            style={kebabItemStyle}
            onMouseEnter={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            <NavIcon name="phone" /> Update Contact
          </button>

          {/* Divider */}
          <div style={{ height: 1, background: 'var(--border)', margin: 'var(--sp-1) 0' }} />

          {/* PRD.203 — Pharmacy moved out of the kebab into the visible
              sidebar tab/action bar. Reachable from the Pharmacy tab,
              the collapsed-rail Pharmacy icon, and /patient/:id/pharmacy
              directly. Workspace setting `sidebarShowPharmacy` controls
              whether the tab is rendered. */}

          {/* Delete Patient */}
          <button
            onClick={() => {
              if (!isAdmin) return;
              setOpen(false);
              onDeletePatient();
            }}
            disabled={!isAdmin}
            title={!isAdmin ? 'Admin only' : undefined}
            style={{
              ...kebabItemStyle,
              color: isAdmin ? 'var(--color-danger)' : 'var(--text-placeholder)',
              cursor: isAdmin ? 'pointer' : 'not-allowed',
              opacity: isAdmin ? 1 : 0.6,
            }}
            onMouseEnter={(e) => {
              if (isAdmin)
                (e.currentTarget as HTMLButtonElement).style.background = 'var(--color-danger-bg)';
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            }}
          >
            <NavIcon name="trash" /> Delete Patient
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Quick Facts Row ───

const QUICK_FACT_CHIP: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
  minWidth: 0,
};

const QUICK_FACT_CIRCLE: React.CSSProperties = {
  width: 40,
  height: 40,
  borderRadius: 'var(--radius-full)',
  background: 'var(--bg-surface-2)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
};

const QUICK_FACT_LABEL: React.CSSProperties = {
  fontSize: 9,
  fontWeight: 600,
  letterSpacing: '0.5px',
  textTransform: 'uppercase',
  color: 'var(--text-muted)',
  lineHeight: 1,
};

function StatsRow({
  status,
  age,
  sex,
  outstandingBalance,
  onViewBilling,
}: {
  status: string | null;
  age: number | null;
  sex: string | null;
  outstandingBalance: SidebarBilling | null;
  onViewBilling: () => void;
}) {
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;
  const bal = outstandingBalance?.total_balance ?? 0;
  const count = outstandingBalance?.unpaid_count ?? 0;
  const isWarning = bal > 0 && (outstandingBalance?.has_overdue === 1 || bal >= 10000);
  const isClear = bal <= 0;
  const isActive = status == null || status.toLowerCase() === 'active';
  const statusLabel = status ?? 'Active';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--sp-4)',
        flexWrap: 'wrap',
      }}
    >
      {/* Gender */}
      {sex &&
        (() => {
          const isMale = sex.toLowerCase() === 'male';
          const bg = isMale ? 'rgba(56, 189, 248, 0.12)' : 'rgba(244, 114, 182, 0.12)';
          const stroke = isMale ? '#0ea5e9' : '#ec4899';
          return (
            <div style={QUICK_FACT_CHIP} title={sex}>
              <div style={{ ...QUICK_FACT_CIRCLE, background: bg }}>
                {isMale ? (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={stroke}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="10" cy="14" r="5" />
                    <line x1="19" y1="5" x2="13.6" y2="10.4" />
                    <polyline points="19 5 19 10" />
                    <polyline points="19 5 14 5" />
                  </svg>
                ) : (
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke={stroke}
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <circle cx="12" cy="9" r="5" />
                    <line x1="12" y1="14" x2="12" y2="22" />
                    <line x1="9" y1="19" x2="15" y2="19" />
                  </svg>
                )}
              </div>
              <span style={QUICK_FACT_LABEL}>Gender</span>
            </div>
          );
        })()}

      {/* Age */}
      {age != null && (
        <div style={QUICK_FACT_CHIP}>
          <div style={QUICK_FACT_CIRCLE}>
            <span
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                color: 'var(--text)',
                lineHeight: 1,
              }}
            >
              {age}
            </span>
          </div>
          <span style={QUICK_FACT_LABEL}>Age</span>
        </div>
      )}

      {/* Balance */}
      <div
        style={{ ...QUICK_FACT_CHIP, cursor: 'pointer' }}
        role="button"
        tabIndex={0}
        onClick={onViewBilling}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onViewBilling();
        }}
        title={
          isClear
            ? 'No outstanding balance'
            : `Outstanding: ${formatBalanceFull(bal, currencySymbol)} \u2022 ${count} unpaid`
        }
      >
        <div
          style={{
            ...QUICK_FACT_CIRCLE,
            background: isClear
              ? 'var(--color-success-bg)'
              : isWarning
                ? 'var(--color-warning-bg)'
                : 'var(--bg-surface-2)',
            color: isClear
              ? 'var(--color-success-text)'
              : isWarning
                ? 'var(--color-warning-text)'
                : 'var(--text)',
            position: 'relative',
          }}
        >
          <span
            style={{
              fontSize: bal >= 1000 ? 10 : 'var(--font-size-sm)',
              fontWeight: 700,
              lineHeight: 1,
            }}
          >
            {formatBalanceCompact(bal, currencySymbol)}
          </span>
          {isClear && (
            <svg
              width="10"
              height="10"
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--color-success-text)"
              strokeWidth="3"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ position: 'absolute', bottom: 4, right: 4 }}
            >
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </div>
        <span style={QUICK_FACT_LABEL}>Balance</span>
      </div>

      {/* Status */}
      <div style={QUICK_FACT_CHIP} title={statusLabel}>
        <div
          style={{
            ...QUICK_FACT_CIRCLE,
            background: isActive ? 'var(--color-success-bg)' : 'var(--bg-surface-2)',
          }}
        >
          <span
            style={{
              width: 12,
              height: 12,
              borderRadius: '50%',
              background: isActive ? 'var(--color-success)' : 'var(--text-muted)',
              display: 'inline-block',
            }}
          />
        </div>
        <span style={QUICK_FACT_LABEL}>Status</span>
      </div>
    </div>
  );
}

// ─── Patient Sidebar ───

type DrawerPanelKey =
  | 'Overview'
  | 'Vitals'
  | 'Visits'
  | 'Treatment Record'
  | 'Allergies'
  | 'Pharmacy'
  | 'Packages';
type DrawerPanel = DrawerPanelKey | null;

const RAIL_TOOLS: readonly { icon: string; label: DrawerPanelKey }[] = [
  { icon: 'layout-grid', label: 'Overview' },
  { icon: 'heart', label: 'Vitals' },
  { icon: 'calendar', label: 'Visits' },
  { icon: 'file-text', label: 'Treatment Record' },
  // PRD.203 — Pharmacy in the collapsed rail opens the right drawer with
  // the same summary card the expanded sidebar shows.
  { icon: 'clipboard', label: 'Pharmacy' },
  // PRD.261 — Patient Packages right-drawer (read-only ownership + balances).
  { icon: 'shopping-bag', label: 'Packages' },
];

function PatientSidebar({
  collapsed,
  onExpand,
  activeDrawer,
  onToggleDrawer,
  patientName,
  initials,
  avatarColor,
  age,
  sex,
  status,
  allergyNames,
  allergyItems,
  contactData,
  intakeStatus,
  intakeCompletedAt,
  intakeSummaryBullets,
  onEditIntake,
  onAddVitals,
  onUpdateMedication,
  onEditFamily,
  onEditContact,
  onEditPersonalInfo,
  onUpdatePhoto,
  onDeletePatient,
  isAdmin,
  photoUrl,
  latestVitals,
  vitalsHistoryRows,
  intakeQuestionnaire,
  intakeAnswers,
  medicationInfo,
  alertNotes,
  recentTreatmentRecords,
  onViewTreatmentRecords,
  patientId,
  userId,
  activeEncounterId,
  onSelectEncounter,
  onCreateEncounter,
  onOpenFormDrilldown,
  onOpenInvoiceDrilldown,
  onOpenFileDrilldown,
  outstandingBalance,
  sidebarTabVisibility,
}: {
  collapsed: boolean;
  onExpand: () => void;
  activeDrawer: DrawerPanel;
  onToggleDrawer: (panel: DrawerPanel) => void;
  patientName: string;
  initials: string;
  avatarColor: string;
  age: number | null;
  sex: string | null;
  status: string | null;
  allergyNames: string[];
  allergyItems: AllergyItem[];
  contactData: ContactData;
  intakeStatus: string | null;
  intakeCompletedAt: string | null;
  intakeSummaryBullets: string[];
  onEditIntake: () => void;
  onAddVitals: () => void;
  onUpdateMedication: () => void;
  onEditFamily: () => void;
  onEditContact: () => void;
  onEditPersonalInfo: () => void;
  onUpdatePhoto: () => void;
  onDeletePatient: () => void;
  isAdmin: boolean;
  photoUrl: string | null;
  latestVitals: LatestVitalsGrouped | null;
  vitalsHistoryRows: PatientVitalEntryWithDefinitionRow[];
  intakeQuestionnaire: IntakeQuestionnaire | null;
  intakeAnswers: IntakeAnswers;
  medicationInfo: MedicationInfo;
  alertNotes: PatientNoteRow[];
  recentTreatmentRecords: PatientTreatmentRecordRow[];
  onViewTreatmentRecords: () => void;
  patientId: string;
  userId: string | null;
  activeEncounterId?: string;
  onSelectEncounter: (id: string) => void;
  onCreateEncounter: () => void;
  onOpenFormDrilldown: (formInstanceId: string, encounterId: string) => void;
  onOpenInvoiceDrilldown: (invoiceId: string, encounterId: string) => void;
  onOpenFileDrilldown: (fileId: string, encounterId: string) => void;
  outstandingBalance: SidebarBilling | null;
  sidebarTabVisibility: SidebarTabVisibility;
}) {
  const [activeTab, setActiveTab] = useState<TabKey>('Overview');
  const [headerCompact, setHeaderCompact] = useState(false);
  const sidebarNavigate = useNavigate();
  const regional = useRegionalSettings();
  const currencySymbol = regional.money_format.currency_symbol;

  // Filter quick-nav tabs and rail tools by the configured visibility.
  const visibleQuickNavTabs = useMemo(
    () => QUICK_NAV_TABS.filter((t) => sidebarTabVisibility[t.label]),
    [sidebarTabVisibility],
  );
  const visibleRailTools = useMemo(
    () => RAIL_TOOLS.filter((t) => sidebarTabVisibility[t.label as TabKey]),
    [sidebarTabVisibility],
  );

  // If the active tab gets hidden by settings, switch to the first visible
  // quick-nav tab so the sidebar always shows valid content.
  useEffect(() => {
    if (sidebarTabVisibility[activeTab]) return;
    const fallback = visibleQuickNavTabs[0]?.label;
    if (fallback) setActiveTab(fallback);
  }, [activeTab, sidebarTabVisibility, visibleQuickNavTabs]);
  const { collapsed: headerManuallyCollapsed, toggle: toggleHeaderCollapsed } = useSidebarState(
    'powercareai:patient-header-collapsed',
  );
  // Either a scroll past the threshold OR an explicit user toggle compacts the header.
  const showCompactHeader = headerCompact || headerManuallyCollapsed;
  const scrollRef = useRef<HTMLDivElement>(null);

  // Compact header when scrollable area is scrolled
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handler = () => setHeaderCompact(el.scrollTop > 10);
    el.addEventListener('scroll', handler, { passive: true });
    return () => el.removeEventListener('scroll', handler);
  }, []);

  // ── Collapsed rail view ──
  if (collapsed) {
    return (
      <aside
        className="shell-sidebar pc-sidebar"
        style={{
          width: SIDEBAR_COLLAPSED_WIDTH,
          height: '100%',
          borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: 'var(--sp-4)',
          gap: 'var(--sp-2)',
        }}
      >
        {/* Mini avatar — click to expand sidebar */}
        <button
          onClick={onExpand}
          title="Expand sidebar"
          style={{
            width: 44,
            height: 44,
            borderRadius: 'var(--radius-full)',
            background: photoUrl ? 'none' : avatarGradient(avatarColor),
            border: '2px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#fff',
            fontSize: '12px',
            fontWeight: 'var(--font-weight-semi)',
            overflow: 'hidden',
            flexShrink: 0,
            cursor: 'pointer',
            padding: 0,
          }}
        >
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={patientName}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            initials
          )}
        </button>

        {/* Quick fact badges — collapsed */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 4,
            marginTop: 2,
          }}
        >
          {sex &&
            (() => {
              const isMale = sex.toLowerCase() === 'male';
              return (
                <div
                  title={sex}
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-full)',
                    background: isMale ? 'rgba(56, 189, 248, 0.12)' : 'rgba(244, 114, 182, 0.12)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {isMale ? (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#0ea5e9"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="10" cy="14" r="5" />
                      <line x1="19" y1="5" x2="13.6" y2="10.4" />
                      <polyline points="19 5 19 10" />
                      <polyline points="19 5 14 5" />
                    </svg>
                  ) : (
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#ec4899"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="12" cy="9" r="5" />
                      <line x1="12" y1="14" x2="12" y2="22" />
                      <line x1="9" y1="19" x2="15" y2="19" />
                    </svg>
                  )}
                </div>
              );
            })()}
          {age != null && (
            <div
              title={`Age: ${age}`}
              style={{
                width: 36,
                height: 36,
                borderRadius: 'var(--radius-full)',
                background: 'var(--bg-surface-2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: 13,
                fontWeight: 700,
                color: 'var(--text)',
              }}
            >
              {age}
            </div>
          )}
          {(() => {
            const bal = outstandingBalance?.total_balance ?? 0;
            const cnt = outstandingBalance?.unpaid_count ?? 0;
            const warn = bal > 0 && (outstandingBalance?.has_overdue === 1 || bal >= 10000);
            const clear = bal <= 0;
            return (
              <div
                role="button"
                tabIndex={0}
                // PRD.219 \u2014 Entry point into Patient Billing Across Visits.
                onClick={() => sidebarNavigate(`/patient/${patientId}/encounters?view=billing`)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter')
                    sidebarNavigate(`/patient/${patientId}/encounters?view=billing`);
                }}
                title={
                  clear
                    ? 'No outstanding balance'
                    : `Outstanding: ${formatBalanceFull(bal, currencySymbol)} \u2022 ${cnt} unpaid`
                }
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 'var(--radius-full)',
                  background: clear
                    ? 'var(--color-success-bg)'
                    : warn
                      ? 'var(--color-warning-bg)'
                      : 'var(--bg-surface-2)',
                  color: clear
                    ? 'var(--color-success-text)'
                    : warn
                      ? 'var(--color-warning-text)'
                      : 'var(--text)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  fontSize: bal >= 1000 ? 9 : 11,
                  fontWeight: 700,
                  cursor: 'pointer',
                  position: 'relative',
                }}
              >
                {formatBalanceCompact(bal, currencySymbol)}
                {clear && (
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="var(--color-success-text)"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    style={{ position: 'absolute', bottom: 2, right: 2 }}
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
              </div>
            );
          })()}
        </div>

        {/* Allergy indicator — click to open Allergies drawer */}
        {allergyNames.length > 0 && (
          <button
            onClick={() => onToggleDrawer('Allergies')}
            title={`Allergies: ${allergyNames.join(', ')}`}
            aria-label="Allergies"
            style={{
              width: 44,
              height: 44,
              borderRadius: 'var(--radius-md)',
              border: 'none',
              background: activeDrawer === 'Allergies' ? 'var(--color-warning-bg)' : 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              color: 'var(--color-warning-text)',
              padding: 0,
              flexShrink: 0,
            }}
          >
            <NavIcon name="alert-triangle" />
          </button>
        )}

        {/* Primary Visit Note CTA — icon-only for the collapsed rail.
            Clicking also opens the Visits drawer so the user sees the
            timeline alongside the opened/created visit. */}
        <PatientVisitNoteCTA
          patientId={patientId}
          onContinue={(id) => {
            if (activeDrawer !== 'Visits') onToggleDrawer('Visits');
            onSelectEncounter(id);
          }}
          onCreate={() => {
            if (activeDrawer !== 'Visits') onToggleDrawer('Visits');
            onCreateEncounter();
          }}
          variant="icon"
        />

        {/* Divider */}
        <div
          style={{
            width: 32,
            height: 1,
            background: 'var(--border)',
            flexShrink: 0,
            margin: 'var(--sp-1) 0',
          }}
        />

        {/* Tool icons — click to open right drawer */}
        {visibleRailTools.map((tool) => {
          const isActive = activeDrawer === tool.label;
          return (
            <button
              key={tool.label}
              onClick={() => onToggleDrawer(tool.label)}
              title={tool.label}
              aria-label={tool.label}
              style={{
                width: 44,
                height: 44,
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: isActive ? 'var(--gradient-nav-active)' : 'transparent',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                color: isActive ? 'var(--color-primary)' : 'var(--text-muted)',
                padding: 0,
                flexShrink: 0,
              }}
            >
              <NavIcon name={tool.icon} />
            </button>
          );
        })}
      </aside>
    );
  }

  return (
    <aside
      className="shell-sidebar pc-sidebar"
      style={{
        width: '100%',
        height: '100%',
        overflowX: 'clip',
        overflowY: 'hidden',
        borderRadius: 'var(--radius-xl) 0 0 var(--radius-xl)',
        flexShrink: 0,
      }}
    >
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
        }}
      >
        {/* Patient Header — compact when scrolled or manually collapsed */}
        <div
          style={{
            padding: showCompactHeader ? 'var(--sp-2) var(--sp-4)' : 'var(--sp-4)',
            flexShrink: 0,
            transition: 'padding 200ms ease',
          }}
        >
          {showCompactHeader ? (
            /* ── Compact header ── */
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                cursor: 'pointer',
              }}
              onClick={() => {
                if (headerManuallyCollapsed) toggleHeaderCollapsed();
                scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
              }}
              title={headerManuallyCollapsed ? 'Expand patient details' : 'Scroll to top to expand'}
            >
              <div
                style={{
                  width: 32,
                  height: 32,
                  borderRadius: 'var(--radius-full)',
                  background: photoUrl ? 'none' : avatarGradient(avatarColor),
                  border: '2px solid var(--border)',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#fff',
                  fontSize: 'var(--font-size-xs)',
                  fontWeight: 'var(--font-weight-semi)',
                  overflow: 'hidden',
                }}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={patientName}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  initials
                )}
              </div>
              <span
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                  flex: 1,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {patientName}
              </span>
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  height: 18,
                  padding: '0 var(--sp-1-5)',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--color-success-bg)',
                  color: 'var(--color-success-text)',
                  fontSize: 10,
                  fontWeight: 500,
                }}
              >
                {status ?? 'Active'}
              </span>
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: 'var(--text-placeholder)', flexShrink: 0 }}
              >
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </div>
          ) : (
            /* ── Full header ── */
            <div
              className="patient-header-container"
              style={{
                padding: 'var(--sp-4)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                background:
                  'linear-gradient(135deg, rgba(245, 240, 235, 0.7) 0%, rgba(230, 240, 250, 0.5) 50%, rgba(240, 235, 245, 0.4) 100%)',
              }}
            >
              {/* Wide: stats sit under the name; narrow: stats reflow to a full-width row.
                  Switch is driven by the @container rule keyed off the wrapper above. */}
              <div className="patient-header-grid">
                <div
                  className="ph-avatar"
                  style={{
                    width: 80,
                    height: 80,
                    borderRadius: 'var(--radius-full)',
                    background: photoUrl ? 'none' : avatarGradient(avatarColor),
                    border: '2px solid var(--border)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontSize: 'var(--font-size-xl)',
                    fontWeight: 'var(--font-weight-semi)',
                    overflow: 'hidden',
                  }}
                >
                  {photoUrl ? (
                    <img
                      src={photoUrl}
                      alt={patientName}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block',
                      }}
                    />
                  ) : (
                    initials
                  )}
                </div>
                <span
                  className="ph-name"
                  style={{
                    fontSize: 'var(--font-size-lg)',
                    fontWeight: 'var(--font-weight-semi)',
                    lineHeight: 'var(--line-height-lg)',
                    letterSpacing: '-0.44px',
                    color: 'var(--text)',
                  }}
                >
                  {patientName}
                </span>
                <div className="ph-kebab">
                  <KebabMenu
                    onEditPersonalInfo={onEditPersonalInfo}
                    onUpdatePhoto={onUpdatePhoto}
                    onUpdateContact={onEditContact}
                    onDeletePatient={onDeletePatient}
                    isAdmin={isAdmin}
                  />
                </div>
                <div className="ph-stats">
                  <StatsRow
                    status={status}
                    age={age}
                    sex={sex}
                    outstandingBalance={outstandingBalance}
                    // PRD.219 — Sidebar stays summary-only. The top-of-page
                    // balance chip is now the entry point into the patient
                    // Billing Across Visits view.
                    onViewBilling={() =>
                      sidebarNavigate(`/patient/${patientId}/encounters?view=billing`)
                    }
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Allergy Banner — sticky */}
        <div
          style={{
            padding: 'var(--sp-3) var(--sp-6)',
            borderBottom: '1px solid var(--color-warning-border)',
            background: 'linear-gradient(to right, var(--color-warning-subtle), var(--bg-surface))',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--sp-2)',
              marginBottom: 'var(--sp-2)',
            }}
          >
            <div style={{ color: 'var(--color-warning-text)' }}>
              <NavIcon name="alert-triangle" />
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-sm)',
                letterSpacing: '0.3px',
                textTransform: 'uppercase',
                color: 'var(--color-warning-text)',
              }}
            >
              Allergies
            </span>
          </div>
          <div style={{ display: 'flex', gap: 'var(--sp-1-5)', flexWrap: 'wrap' }}>
            {allergyNames.map((allergy) => (
              <span
                key={allergy}
                style={{
                  height: 22,
                  padding: '0 var(--sp-2)',
                  borderRadius: 'var(--radius-full)',
                  background: 'var(--bg-surface)',
                  border: '1px solid var(--color-warning-border)',
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: '22px',
                  color: 'var(--color-warning-text)',
                }}
              >
                {allergy}
              </span>
            ))}
          </div>
        </div>

        {/* Primary Visit Note CTA — stays visible across all tabs.
            Compacts together with the patient header on scroll.
            Clicking also flips the sidebar to the Visits tab so the user
            immediately sees the timeline for orientation. */}
        <div
          style={{
            padding: showCompactHeader ? 'var(--sp-2) var(--sp-4) 0' : 'var(--sp-3) var(--sp-6) 0',
            flexShrink: 0,
            transition: 'padding 200ms ease',
          }}
        >
          <PatientVisitNoteCTA
            patientId={patientId}
            onContinue={(id) => {
              setActiveTab('Visits');
              onSelectEncounter(id);
            }}
            onCreate={() => {
              setActiveTab('Visits');
              onCreateEncounter();
            }}
          />
          {/* PRD.258 — send the WhatsApp booking link to this patient. */}
          <div style={{ marginTop: 'var(--sp-2)' }}>
            <SendWhatsAppBookingFlowAction
              patient={{ id: patientId, name: patientName }}
              patientPhone={contactData.phone || null}
              fullWidth
            />
          </div>
        </div>

        {/* Quick Navigation Tabs — sticky */}
        <div
          style={{
            padding: 'var(--sp-4) var(--sp-6)',
            borderBottom: '1px solid var(--border)',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              background: 'var(--bg-surface-2)',
              borderRadius: 'var(--radius-md)',
              padding: 'var(--sp-1)',
              gap: 'var(--sp-1)',
            }}
          >
            {visibleQuickNavTabs.map((tab) => {
              const isActive = tab.label === activeTab;
              return (
                <button
                  key={tab.label}
                  onClick={() => setActiveTab(tab.label)}
                  style={{
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 'var(--sp-1)',
                    padding: 'var(--sp-2) 0',
                    borderRadius: 'var(--radius-sm)',
                    background: isActive ? 'var(--bg-surface)' : 'transparent',
                    boxShadow: isActive ? 'var(--shadow-sm)' : 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: isActive ? 'var(--text)' : 'var(--text-placeholder)',
                    position: 'relative',
                  }}
                >
                  <div style={{ position: 'relative' }}>
                    <NavIcon name={tab.icon} />
                    {tab.notification && (
                      <div
                        style={{
                          position: 'absolute',
                          top: -2,
                          right: -4,
                          width: 8,
                          height: 8,
                          borderRadius: 'var(--radius-full)',
                          background: 'var(--color-success)',
                        }}
                      />
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                      lineHeight: 'var(--line-height-tight)',
                      letterSpacing: '0.12px',
                    }}
                  >
                    {tab.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Scrollable Card Area */}
        <div
          ref={scrollRef}
          style={{
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: 'var(--sp-6)',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-4)',
          }}
        >
          {activeTab === 'Overview' && (
            <>
              <PatientDetailsCard contactData={contactData} onEdit={onEditContact} />
              {/* PRD.218 — Billing summary (shared aggregation with sidebar chip).
                  PRD.219 — "View invoices →" navigates to the patient
                  Billing Across Visits page, not the Treatment Record.
                  Placed directly under Patient Details so balance / charged
                  totals sit near identity context. */}
              <PatientBillingSummaryCard
                patientId={patientId}
                onView={() => sidebarNavigate(`/patient/${patientId}/encounters?view=billing`)}
              />
              <IntakeFormCard
                status={intakeStatus}
                completedAt={intakeCompletedAt}
                summaryBullets={intakeSummaryBullets}
                onEdit={onEditIntake}
              />
              <VitalsCard latestVitals={latestVitals} onAdd={onAddVitals} />
              <AllergiesCard allergyItems={allergyItems} onEdit={onUpdateMedication} />
              {/* PRD.267 — family allergy/medication context, kept visually
                  separate from the patient's own allergies above. Editable via
                  the Family step editor in a drawer. */}
              <FamilyHistorySection patientId={patientId} variant="card" onEdit={onEditFamily} />
              <TreatmentRecordCard
                records={recentTreatmentRecords}
                onAdd={onViewTreatmentRecords}
                onViewAll={onViewTreatmentRecords}
              />
              {alertNotes.length > 0 && <PatientAlertsCard notes={alertNotes} />}
              <EmergencyContactCard contactData={contactData} onEdit={onEditContact} />
            </>
          )}
          {activeTab === 'Intake Form' && (
            <IntakeFormDetailView
              onEditClick={onEditIntake}
              questionnaire={intakeQuestionnaire}
              answers={intakeAnswers}
              intakeStatus={intakeStatus}
            />
          )}
          {activeTab === 'Vitals' && (
            <VitalsDetailView
              onAddVitals={onAddVitals}
              latestVitals={latestVitals}
              historyRows={vitalsHistoryRows}
            />
          )}
          {activeTab === 'Allergies' && (
            <>
              <AllergiesDetailView
                onUpdateMedication={onUpdateMedication}
                allergies={allergyItems}
                medicationInfo={medicationInfo}
              />
              <FamilyHistorySection patientId={patientId} onEdit={onEditFamily} />
            </>
          )}
          {activeTab === 'Visits' && (
            <SidebarVisitsPanel
              patientId={patientId}
              selectedEncounterId={activeEncounterId}
              onSelectEncounter={onSelectEncounter}
              onOpenFormDrilldown={onOpenFormDrilldown}
              onOpenInvoiceDrilldown={onOpenInvoiceDrilldown}
              onOpenFileDrilldown={onOpenFileDrilldown}
            />
          )}
          {activeTab === 'Treatment Record' && (
            <TreatmentRecordSidebarView
              patientId={patientId}
              onAdd={onViewTreatmentRecords}
              onViewAll={onViewTreatmentRecords}
            />
          )}
          {activeTab === 'Pharmacy' && (
            <SidebarPharmacyPanel
              patientId={patientId}
              onViewAll={() => sidebarNavigate(`/patient/${patientId}/pharmacy`)}
            />
          )}
          {activeTab === 'Packages' && (
            <SidebarPackagesPanel
              patientId={patientId}
              userId={userId}
              isAdmin={isAdmin}
              activeEncounterId={activeEncounterId ?? null}
              onViewAll={() => sidebarNavigate(`/patient/${patientId}/packages`)}
            />
          )}
          {activeTab === 'Contact' && (
            <ContactDetailView
              contact={contactData}
              onEditContact={onEditContact}
              patientId={patientId}
            />
          )}
        </div>
      </div>
    </aside>
  );
}

// ─── Sidebar Info Cards ───

// ─── Card Edit Pencil Button ───

const cardEditBtnStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 24,
  height: 24,
  borderRadius: 'var(--radius-full)',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  padding: 0,
  marginLeft: 'auto',
  flexShrink: 0,
};

function CardEditButton({ onClick }: { onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      title="Edit"
      style={cardEditBtnStyle}
      onMouseEnter={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
        (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)';
      }}
      onMouseLeave={(e) => {
        (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
        (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
      }}
    >
      <NavIcon name="edit" />
    </button>
  );
}

function PatientDetailsCard({
  contactData,
  onEdit,
}: {
  contactData: ContactData;
  onEdit: () => void;
}) {
  const addressText = contactData.street
    ? [
        contactData.street,
        [contactData.city, contactData.state, contactData.zip].filter(Boolean).join(', '),
      ]
        .filter(Boolean)
        .join('\n')
    : 'No address specified';
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
        background: 'linear-gradient(to bottom, var(--bg-surface-2), var(--bg-surface))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ color: 'var(--text-muted)' }}>
          <NavIcon name="user" />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          Patient Details
        </span>
        <CardEditButton onClick={onEdit} />
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-3)',
        }}
      >
        <ContactRow icon="map-pin" text={addressText} />
        <ContactRow icon="phone" text={contactData.phone || 'No phone specified'} />
        <ContactRow icon="mail" text={contactData.email || 'No email specified'} />
      </div>
    </div>
  );
}

function IntakeFormCard({
  status,
  completedAt,
  summaryBullets,
  onEdit,
}: {
  status: string | null;
  completedAt: string | null;
  summaryBullets: string[];
  onEdit: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-info-border)',
        padding: 'var(--sp-4)',
        background: 'linear-gradient(to bottom, var(--color-info-bg), var(--bg-surface))',
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ color: 'var(--color-info)' }}>
          <NavIcon name="clipboard" />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          Intake Form
        </span>
        <CardEditButton onClick={onEdit} />
      </div>

      {/* Status + Date */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        {status && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 20,
              padding: '0 var(--sp-2)',
              borderRadius: 'var(--radius-sm)',
              background:
                status === 'completed' ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
              color:
                status === 'completed' ? 'var(--color-success-text)' : 'var(--color-warning-text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-sm)',
              textTransform: 'capitalize',
            }}
          >
            {status === 'completed' ? 'Complete' : status}
          </span>
        )}
        {completedAt && (
          <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
            {formatDateTime(completedAt)}
          </span>
        )}
      </div>

      {/* Content */}
      <div
        style={{
          fontSize: 'var(--font-size-sm)',
          lineHeight: 'var(--line-height-sm)',
          color: 'var(--text-muted)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--sp-1)',
        }}
      >
        {summaryBullets.length > 0 ? (
          summaryBullets.map((bullet, i) => <div key={i}>&bull; {bullet}</div>)
        ) : (
          <div style={{ color: 'var(--text-placeholder)' }}>No intake data yet</div>
        )}
      </div>
    </div>
  );
}

// ─── Intake Form Detail View (sidebar tab) ───

function formatAnswerDisplay(
  answer: string | string[] | undefined,
  question: { type: string; options?: { label: string; value: string }[] },
): string {
  if (answer === undefined || answer === null || answer === '') return '—';
  if (Array.isArray(answer)) {
    if (answer.length === 0) return '—';
    // For multi-select, resolve option labels
    if (question.options) {
      return answer.map((v) => question.options!.find((o) => o.value === v)?.label ?? v).join(', ');
    }
    return answer.join(', ');
  }
  // For yes-no
  if (question.type === 'yes-no') {
    if (answer === 'yes' || answer === 'true' || answer === '1') return 'Yes';
    if (answer === 'no' || answer === 'false' || answer === '0') return 'No';
    return answer;
  }
  // For select, resolve option label
  if (question.type === 'select' && question.options) {
    return question.options.find((o) => o.value === answer)?.label ?? answer;
  }
  // For signature, show "Signed" instead of raw stroke JSON
  if (question.type === 'signature') {
    return answer && answer !== '{}' ? 'Signed ✓' : '—';
  }
  // Skip long JSON blobs (safety net)
  const strVal = String(answer);
  if (strVal.startsWith('{"version":') || strVal.startsWith('[{') || strVal.length > 200) {
    return '—';
  }
  return strVal;
}

function IntakeFormDetailView({
  onEditClick,
  questionnaire,
  answers,
  intakeStatus,
}: {
  onEditClick?: () => void;
  questionnaire: IntakeQuestionnaire | null;
  answers: IntakeAnswers;
  intakeStatus: string | null;
}) {
  const sectionTitleStyle: CSSProperties = {
    fontSize: 'var(--font-size-sm)',
    fontWeight: 'var(--font-weight-semi)',
    lineHeight: 'var(--line-height-sm)',
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
    color: 'var(--text)',
    marginBottom: 'var(--sp-2)',
  };

  const rowStyle: CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'baseline',
    fontSize: 'var(--font-size-sm)',
    lineHeight: 'var(--line-height-sm)',
    padding: 'var(--sp-1) 0',
  };

  const labelStyle: CSSProperties = { color: 'var(--text-muted)' };
  const valueStyle: CSSProperties = {
    color: 'var(--text)',
    fontWeight: 'var(--font-weight-medium)',
    textAlign: 'right',
    maxWidth: '55%',
  };

  const isComplete = intakeStatus === 'completed';
  const sections = questionnaire?.sections ?? [];

  return (
    <>
      {/* Header Card */}
      <div
        style={{
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--color-info-border)',
          padding: 'var(--sp-4)',
          background: 'linear-gradient(to bottom, var(--color-info-bg), var(--bg-surface))',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--sp-3)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <div style={{ color: 'var(--color-info)' }}>
              <NavIcon name="clipboard" />
            </div>
            <span
              style={{
                fontSize: 'var(--font-size-base)',
                fontWeight: 'var(--font-weight-semi)',
                lineHeight: 'var(--line-height-base)',
                letterSpacing: '-0.15px',
                color: 'var(--text)',
              }}
            >
              Intake Form
            </span>
          </div>
          <button
            onClick={onEditClick}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--sp-1)',
              height: 26,
              padding: '0 var(--sp-3)',
              borderRadius: 'var(--radius-full)',
              border: '1px solid var(--color-info-border)',
              background: 'transparent',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--color-info)',
              cursor: 'pointer',
            }}
          >
            <NavIcon name="edit" /> Edit Form
          </button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              height: 20,
              padding: '0 var(--sp-2)',
              borderRadius: 'var(--radius-sm)',
              background: isComplete ? 'var(--color-success-bg)' : 'var(--color-warning-bg)',
              color: isComplete ? 'var(--color-success-text)' : 'var(--color-warning-text)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 'var(--font-weight-medium)',
              lineHeight: 'var(--line-height-sm)',
              textTransform: 'capitalize',
            }}
          >
            {isComplete ? 'Complete' : (intakeStatus ?? 'Pending')}
          </span>
          {questionnaire && (
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                height: 20,
                padding: '0 var(--sp-2)',
                borderRadius: 'var(--radius-sm)',
                background: 'var(--bg-surface-2)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-medium)',
                lineHeight: 'var(--line-height-sm)',
              }}
            >
              v{questionnaire.version}
            </span>
          )}
        </div>
      </div>

      {/* Dynamic Sections from questionnaire */}
      {sections.map((section) => {
        const answeredQuestions = section.questions.filter((q) => {
          const a = answers[q.fieldKey];
          return a !== undefined && a !== null && a !== '' && !(Array.isArray(a) && a.length === 0);
        });
        // Skip sections with no answers
        if (answeredQuestions.length === 0 && Object.keys(answers).length > 0) return null;
        const questionsToShow =
          answeredQuestions.length > 0 ? answeredQuestions : section.questions;

        return (
          <div key={section.id}>
            <div style={sectionTitleStyle}>{section.title}</div>
            <div
              style={{
                borderRadius: 'var(--radius-lg)',
                border: '1px solid var(--border)',
                padding: 'var(--sp-3) var(--sp-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-1)',
              }}
            >
              {questionsToShow.map((question, qi) => {
                const display = formatAnswerDisplay(answers[question.fieldKey], question);
                const isLong = display.length > 40 || question.type === 'textarea';
                return (
                  <div key={question.id}>
                    {qi > 0 && (
                      <div
                        style={{
                          borderTop: '1px solid var(--border-subtle, var(--border))',
                          margin: 'var(--sp-1) 0',
                        }}
                      />
                    )}
                    <div
                      style={
                        isLong
                          ? {
                              ...rowStyle,
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              gap: 'var(--sp-1)',
                            }
                          : rowStyle
                      }
                    >
                      <span style={labelStyle}>{question.label}:</span>
                      <span
                        style={
                          isLong
                            ? { ...valueStyle, textAlign: 'left', maxWidth: '100%' }
                            : valueStyle
                        }
                      >
                        {display}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* No data fallback */}
      {sections.length === 0 && (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-placeholder)',
            fontStyle: 'italic',
          }}
        >
          No intake form definition available.
        </div>
      )}
    </>
  );
}

function VitalsCard({
  latestVitals,
  onAdd,
}: {
  latestVitals: LatestVitalsGrouped | null;
  onAdd: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-danger-border)',
        padding: 'var(--sp-4)',
        background: 'linear-gradient(to bottom, var(--color-danger-bg), var(--bg-surface))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ color: 'var(--color-danger)' }}>
          <NavIcon name="heart" />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          Vitals
        </span>
        <button
          onClick={onAdd}
          title="Record Vitals"
          style={{
            ...cardEditBtnStyle,
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
          }}
        >
          <NavIcon name="plus" />
        </button>
      </div>

      {latestVitals ? (
        <>
          <div
            style={{
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
              marginBottom: 'var(--sp-3)',
            }}
          >
            {formatRecordedAt(latestVitals.recordedAt)}
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 'var(--sp-2)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            {latestVitals.primary.slice(0, 4).map((row) => (
              <div key={row.id}>
                <span style={{ color: 'var(--text-muted)' }}>{row.name}: </span>
                <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                  {formatVitalValue(row)}
                  {row.unit ? ` ${row.unit}` : ''}
                </span>
              </div>
            ))}
          </div>
        </>
      ) : (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-muted)',
            fontStyle: 'italic',
          }}
        >
          No vitals recorded
        </div>
      )}
    </div>
  );
}

function AllergiesCard({
  allergyItems,
  onEdit,
}: {
  allergyItems: AllergyItem[];
  onEdit: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-warning-border)',
        padding: 'var(--sp-4)',
        background: 'linear-gradient(to bottom, var(--color-warning-subtle), var(--bg-surface))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ color: 'var(--color-warning-text)' }}>
          <NavIcon name="alert-triangle" />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          Allergies
        </span>
        <CardEditButton onClick={onEdit} />
      </div>

      {allergyItems.length === 0 ? (
        <div
          style={{
            fontSize: 'var(--font-size-sm)',
            color: 'var(--text-placeholder)',
          }}
        >
          No known allergies
        </div>
      ) : (
        <div style={{ display: 'flex', gap: 'var(--sp-2)', flexWrap: 'wrap' }}>
          {allergyItems.map((a) => (
            <div
              key={a.name}
              style={{
                borderRadius: 'var(--radius-md)',
                border: '1px solid var(--color-warning-border)',
                padding: '5px 11px',
              }}
            >
              <div
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  lineHeight: 'var(--line-height-sm)',
                  color: 'var(--text)',
                }}
              >
                {a.name}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  lineHeight: 'var(--line-height-tight)',
                  letterSpacing: '0.12px',
                  color: 'var(--color-warning-text)',
                  textTransform: 'capitalize',
                }}
              >
                {a.severity}
                {a.reaction ? ` \u2022 ${a.reaction}` : ''}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const ALERT_BADGE_STYLES: Record<string, { bg: string; color: string }> = {
  info: { bg: '#dbeafe', color: '#1d4ed8' },
  warning: { bg: '#fef3c7', color: '#92400e' },
  critical: { bg: '#fee2e2', color: '#dc2626' },
};

function TreatmentRecordCard({
  records,
  onAdd,
  onViewAll,
}: {
  records: PatientTreatmentRecordRow[];
  onAdd: () => void;
  onViewAll: () => void;
}) {
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
        background: 'var(--bg-surface)',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ color: 'var(--text-muted)' }}>
          <NavIcon name="clipboard" />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          Treatment Record
        </span>
        <button
          onClick={onAdd}
          title="Add Record"
          style={cardEditBtnStyle}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-surface-2)';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text)';
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
            (e.currentTarget as HTMLButtonElement).style.color = 'var(--text-muted)';
          }}
        >
          <NavIcon name="plus" />
        </button>
      </div>

      {records.length === 0 ? (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-placeholder)' }}>
          No treatment records
        </span>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {records.map((r) => (
            <div
              key={r.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 'var(--font-size-sm)',
              }}
            >
              <span
                style={{
                  color: 'var(--text-muted)',
                  fontSize: 'var(--font-size-xs)',
                  width: 70,
                  flexShrink: 0,
                }}
              >
                {r.date
                  ? new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                    })
                  : '—'}
              </span>
              {r.tooth_nos && (
                <span
                  style={{
                    color: 'var(--text-placeholder)',
                    fontSize: 'var(--font-size-xs)',
                    flexShrink: 0,
                  }}
                >
                  {r.tooth_nos}
                </span>
              )}
              <span
                style={{
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {r.procedure_name}
              </span>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={onViewAll}
        style={{
          marginTop: 'var(--sp-3)',
          background: 'none',
          border: 'none',
          padding: 0,
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-primary)',
          cursor: 'pointer',
          fontWeight: 'var(--font-weight-medium)',
        }}
      >
        View in Encounters &gt;
      </button>
    </div>
  );
}

function TreatmentRecordSidebarView({
  patientId,
  onAdd,
  onViewAll,
}: {
  patientId: string;
  onAdd: () => void;
  onViewAll: () => void;
}) {
  const fmtMoney = useFormatMoney();
  // Query the same data as the main TreatmentRecordView so the sidebar
  // surfaces day-grouped procedure rows, combined visit notes, and
  // note-only / invoice-only days — instead of just a flat list of
  // procedures.
  const { data: records = [] } = usePowerSyncQuery<PatientTreatmentRecordRow>(
    qTreatmentRecordsByPatient(patientId),
  );
  const { data: encounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    qEncountersByPatient(patientId),
  );
  const { data: notesPreviews = [] } = usePowerSyncQuery<EncounterNotePreviewRow>(
    qEncounterNotesPreviews(patientId),
  );
  const { data: invoiceRows = [] } = usePowerSyncQuery<InvoiceRow>(qInvoicesByPatient(patientId));

  const notesByEncounter = useMemo(() => {
    const map: Record<string, EncounterNotePreviewRow[]> = {};
    for (const n of notesPreviews) {
      if (!map[n.encounter_id]) map[n.encounter_id] = [];
      map[n.encounter_id].push(n);
    }
    return map;
  }, [notesPreviews]);

  const invoiceToEncounter = useMemo(() => {
    const map: Record<string, string> = {};
    for (const inv of invoiceRows) {
      if (inv.encounter_id) map[inv.id] = inv.encounter_id;
    }
    return map;
  }, [invoiceRows]);

  const encountersByDate = useMemo(() => {
    const map: Record<string, PatientEncounterRow[]> = {};
    for (const e of encounterRows) {
      const d = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!d) continue;
      if (!map[d]) map[d] = [];
      map[d].push(e);
    }
    return map;
  }, [encounterRows]);

  const resolveEncounterIds = useCallback(
    (r: PatientTreatmentRecordRow): string[] => {
      if (r.encounter_id) return [r.encounter_id];
      if (r.invoice_id && invoiceToEncounter[r.invoice_id]) {
        return [invoiceToEncounter[r.invoice_id]];
      }
      const sameDay = encountersByDate[r.date];
      if (sameDay && sameDay.length > 0) return sameDay.map((e) => e.id);
      return [];
    },
    [invoiceToEncounter, encountersByDate],
  );

  const recordsByDate = useMemo(() => {
    const groups: { date: string; rows: PatientTreatmentRecordRow[] }[] = [];
    const seenDates = new Set<string>();
    for (const r of records) {
      const last = groups[groups.length - 1];
      if (last && last.date === r.date) last.rows.push(r);
      else groups.push({ date: r.date, rows: [r] });
      seenDates.add(r.date);
    }
    const extraDates = new Set<string>();
    const noteEncIds = new Set(notesPreviews.map((n) => n.encounter_id));
    const invEncIds = new Set(
      invoiceRows.filter((i) => i.encounter_id).map((i) => i.encounter_id as string),
    );
    for (const e of encounterRows) {
      const d = (e.started_at ?? e.created_at ?? '').slice(0, 10);
      if (!d || seenDates.has(d)) continue;
      if (noteEncIds.has(e.id) || invEncIds.has(e.id)) extraDates.add(d);
    }
    for (const d of extraDates) groups.push({ date: d, rows: [] });
    groups.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    return groups;
  }, [records, notesPreviews, invoiceRows, encounterRows]);

  const collectDayNotes = useCallback(
    (date: string, rows: PatientTreatmentRecordRow[]): EncounterNotePreviewRow[] => {
      const seen = new Set<string>();
      const out: EncounterNotePreviewRow[] = [];
      for (const r of rows) {
        for (const eid of resolveEncounterIds(r)) {
          for (const n of notesByEncounter[eid] ?? []) {
            const key = n.created_at ?? `${n.encounter_id}:${n.title}:${n.body}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(n);
          }
        }
      }
      for (const e of encountersByDate[date] ?? []) {
        for (const n of notesByEncounter[e.id] ?? []) {
          const key = n.created_at ?? `${n.encounter_id}:${n.title}:${n.body}`;
          if (seen.has(key)) continue;
          seen.add(key);
          out.push(n);
        }
      }
      return out;
    },
    [notesByEncounter, resolveEncounterIds, encountersByDate],
  );

  const totalProcedures = records.length;

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-4)',
        background: 'var(--bg-surface)',
      }}
    >
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div>
          <div
            style={{
              fontSize: 'var(--font-size-base)',
              fontWeight: 'var(--font-weight-semi)',
              color: 'var(--text)',
            }}
          >
            Treatment Record
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
            Recent procedures
          </div>
        </div>
        <button
          onClick={onAdd}
          style={{
            height: 28,
            padding: '0 10px',
            borderRadius: 'var(--radius-md)',
            border: 'none',
            background: 'var(--color-success, #16a34a)',
            color: '#fff',
            fontSize: 'var(--font-size-xs)',
            fontWeight: 'var(--font-weight-semi)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 4,
          }}
        >
          <NavIcon name="plus" /> Add Record
        </button>
      </div>

      {/* Totals summary — always visible */}
      {(() => {
        const totalCharged = records.reduce((s, r) => s + (r.amount_charged_cents ?? 0), 0) / 100;
        const totalPaid = records.reduce((s, r) => s + (r.amount_paid_cents ?? 0), 0) / 100;
        const totalBalance = totalCharged - totalPaid;
        const fmt = (v: number) => fmtMoney(v);
        return (
          <div
            style={{
              display: 'flex',
              gap: 'var(--sp-3)',
              fontSize: 'var(--font-size-xs)',
              flexWrap: 'wrap',
            }}
          >
            <span style={{ color: 'var(--text-muted)' }}>
              Total Charged: <strong style={{ color: 'var(--text)' }}>{fmt(totalCharged)}</strong>
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              Total Paid:{' '}
              <strong style={{ color: 'var(--color-success-text)' }}>{fmt(totalPaid)}</strong>
            </span>
            <span style={{ color: 'var(--text-muted)' }}>
              Total Balance:{' '}
              <strong
                style={{ color: totalBalance > 0 ? 'var(--color-warning-text)' : 'var(--text)' }}
              >
                {fmt(totalBalance)}
              </strong>
            </span>
          </div>
        );
      })()}

      {/* Full table — same columns as main TreatmentRecordView. The
          sidebar is narrow so we wrap the table in a horizontal scroll
          container; cells stay on one line so columns stay aligned. */}
      {recordsByDate.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: 'var(--sp-6)',
            color: 'var(--text-muted)',
            fontSize: 'var(--font-size-sm)',
            border: '2px dashed var(--border)',
            borderRadius: 'var(--radius-lg)',
          }}
        >
          No treatment records
        </div>
      ) : (
        <>
          <div
            style={{
              border: '1px solid var(--border)',
              borderRadius: 'var(--radius-md)',
              overflowX: 'auto',
              overflowY: 'hidden',
              background: 'var(--bg-surface)',
            }}
          >
            <table style={{ borderCollapse: 'collapse', minWidth: 720 }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {[
                    { label: 'Date' },
                    { label: 'Part #' },
                    { label: 'Procedure' },
                    { label: 'Notes', minWidth: 200 },
                    { label: 'Charged', align: 'right' as const },
                    { label: 'Paid', align: 'right' as const },
                    { label: 'Balance', align: 'right' as const },
                    { label: 'Next Appt' },
                  ].map((col) => (
                    <th
                      key={col.label}
                      style={{
                        padding: '8px 10px',
                        textAlign: col.align ?? ('left' as const),
                        fontSize: 10,
                        fontWeight: 600,
                        color: 'var(--text-placeholder)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                        whiteSpace: 'nowrap',
                        minWidth: col.minWidth,
                      }}
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recordsByDate.map(({ date, rows }) => {
                  const dayNotes = collectDayNotes(date, rows);
                  return (
                    <Fragment key={date}>
                      {dayNotes.length > 0 && (
                        <tr style={{ background: 'var(--bg-surface-2, #f9fafb)' }}>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text)',
                              fontWeight: 'var(--font-weight-medium)',
                              whiteSpace: 'nowrap',
                              verticalAlign: 'top',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            {new Date(date + 'T00:00:00').toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })}
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-placeholder)',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-placeholder)',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text)',
                              whiteSpace: 'normal',
                              verticalAlign: 'top',
                              minWidth: 200,
                              maxWidth: 320,
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 'var(--sp-2)',
                              }}
                            >
                              {dayNotes.map((n, ni) => (
                                <NotePreview key={n.created_at ?? `${ni}`} note={n} marker="dot" />
                              ))}
                            </div>
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-placeholder)',
                              textAlign: 'right',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-placeholder)',
                              textAlign: 'right',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-placeholder)',
                              textAlign: 'right',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            —
                          </td>
                          <td
                            style={{
                              padding: '8px 10px',
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-placeholder)',
                              borderBottom: '1px solid var(--border)',
                            }}
                          >
                            —
                          </td>
                        </tr>
                      )}
                      {rows.map((r) => {
                        const balance = (r.amount_charged_cents ?? 0) - (r.amount_paid_cents ?? 0);
                        const fmt = (cents: number) => fmtMoney(cents / 100);
                        return (
                          <tr key={r.id} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--text)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {new Date(r.date + 'T00:00:00').toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric',
                              })}
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--text-muted)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {r.tooth_nos || '—'}
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--text)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {r.procedure_name}
                            </td>
                            <td style={{ padding: '8px 10px' }} />
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--text)',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {fmt(r.amount_charged_cents ?? 0)}
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-success-text, #16a34a)',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {fmt(r.amount_paid_cents ?? 0)}
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                fontWeight: 'var(--font-weight-semi)',
                                color:
                                  balance > 0 ? '#dc2626' : balance < 0 ? '#16a34a' : 'var(--text)',
                                textAlign: 'right',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {fmt(balance)}
                            </td>
                            <td
                              style={{
                                padding: '8px 10px',
                                fontSize: 'var(--font-size-xs)',
                                color: r.next_appointment_at
                                  ? 'var(--text)'
                                  : 'var(--text-placeholder)',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {r.next_appointment_at
                                ? new Date(
                                    r.next_appointment_at.split('T')[0] + 'T00:00:00',
                                  ).toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric',
                                  })
                                : '—'}
                            </td>
                          </tr>
                        );
                      })}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Footer */}
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            <span style={{ color: 'var(--text-muted)' }}>
              {totalProcedures} procedure{totalProcedures !== 1 ? 's' : ''} · {recordsByDate.length}{' '}
              day{recordsByDate.length !== 1 ? 's' : ''}
            </span>
            <button
              onClick={onViewAll}
              style={{
                background: 'none',
                border: 'none',
                padding: 0,
                color: 'var(--color-primary)',
                cursor: 'pointer',
                fontWeight: 'var(--font-weight-medium)',
                fontSize: 'var(--font-size-xs)',
              }}
            >
              View in Encounters &gt;
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function PatientAlertsCard({ notes }: { notes: PatientNoteRow[] }) {
  return (
    <div
      style={{
        background: 'var(--bg-surface)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 'var(--sp-3)' }}>
        <span style={{ color: '#dc2626', display: 'flex' }}>
          <NavIcon name="alert-triangle" />
        </span>
        <span
          style={{
            fontSize: 'var(--font-size-sm)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          Patient Alerts
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {notes.map((n) => {
          const badge = ALERT_BADGE_STYLES[n.alert_level ?? 'info'] ?? ALERT_BADGE_STYLES.info;
          return (
            <div
              key={n.id}
              style={{
                padding: '6px 8px',
                borderRadius: 'var(--radius-sm)',
                background: badge.bg,
                border: `1px solid ${badge.color}20`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 2 }}>
                <span
                  style={{
                    fontSize: 9,
                    padding: '0 5px',
                    borderRadius: 'var(--radius-xs)',
                    background: badge.color,
                    color: '#fff',
                    fontWeight: 'var(--font-weight-semi)',
                    textTransform: 'uppercase',
                  }}
                >
                  {n.alert_level}
                </span>
                {n.author_name && (
                  <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                    {n.author_name}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: 'var(--font-size-xs)',
                  color: 'var(--text)',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {(n.body ?? '').slice(0, 80)}
                {(n.body ?? '').length > 80 ? '...' : ''}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function EmergencyContactCard({
  contactData,
  onEdit,
}: {
  contactData: ContactData;
  onEdit: () => void;
}) {
  const hasEmergency = contactData.emergencyName || contactData.emergencyPhone;
  return (
    <div
      style={{
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border)',
        padding: 'var(--sp-4)',
        background: 'linear-gradient(to bottom, var(--bg-surface-2), var(--bg-surface))',
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--sp-2)',
          marginBottom: 'var(--sp-3)',
        }}
      >
        <div style={{ color: 'var(--text-muted)' }}>
          <NavIcon name="users" />
        </div>
        <span
          style={{
            fontSize: 'var(--font-size-base)',
            fontWeight: 'var(--font-weight-semi)',
            lineHeight: 'var(--line-height-base)',
            letterSpacing: '-0.15px',
            color: 'var(--text)',
          }}
        >
          Emergency Contact
        </span>
        <CardEditButton onClick={onEdit} />
      </div>

      {hasEmergency ? (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
            fontSize: 'var(--font-size-sm)',
            lineHeight: 'var(--line-height-sm)',
            color: 'var(--text)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
            <NavIcon name="user" />
            <span>{contactData.emergencyName}</span>
            {contactData.emergencyRelationship && (
              <span style={{ color: 'var(--text-muted)' }}>
                ({contactData.emergencyRelationship})
              </span>
            )}
          </div>
          {contactData.emergencyPhone && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--sp-2)' }}>
              <NavIcon name="phone" />
              <span>{contactData.emergencyPhone}</span>
            </div>
          )}
        </div>
      ) : (
        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-placeholder)' }}>
          No emergency contact specified
        </span>
      )}
    </div>
  );
}

// ─── Main Layout ───

export function PatientShellLayout() {
  const { id: patientId } = useParams<{ id: string }>();
  const location = useLocation();
  // Extract current encounter ID from URL: /patient/:id/encounters/:encounterId
  const activeEncounterId = useMemo(() => {
    const match = location.pathname.match(/\/patient\/[^/]+\/encounters\/([^/]+)/);
    return match ? match[1] : undefined;
  }, [location.pathname]);
  // Patient sidebar collapsed state — session-only, per patient (PRD.181 §10).
  // Each open patient tab keeps its own runtime collapsed/expanded state in
  // memory. When a patient is opened for the first time (or reopened after
  // its tab was closed) the state is seeded from the clinic-configured
  // Default Sidebar State. Toggles only mutate the current patient's slot;
  // other tabs are unaffected. Closed tabs are pruned so reopening re-applies
  // the configured default.
  const [sidebarStateByPatient, setSidebarStateByPatient] = useState<Record<string, boolean>>({});
  const collapsed = patientId ? (sidebarStateByPatient[patientId] ?? false) : false;
  const toggle = useCallback(() => {
    if (!patientId) return;
    setSidebarStateByPatient((prev) => ({
      ...prev,
      [patientId]: !(prev[patientId] ?? false),
    }));
  }, [patientId]);

  const { data: workspaceSettingsRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  // Track which patient ids have already had the clinic default applied so
  // we don't clobber the user's mid-session toggle on subsequent renders.
  const sidebarAppliedForRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!patientId) return;
    if (sidebarAppliedForRef.current.has(patientId)) return;
    if (workspaceSettingsRows.length === 0) return;
    const ui = mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]);
    const desired = ui.defaultSidebarState === 'collapsed';
    setSidebarStateByPatient((prev) => ({ ...prev, [patientId]: desired }));
    sidebarAppliedForRef.current.add(patientId);
  }, [patientId, workspaceSettingsRows]);

  // Build the sidebar tab visibility map from the workspace settings
  // (PRD.181). Falls back to "all visible" until the settings row arrives.
  const sidebarTabVisibility = useMemo<SidebarTabVisibility>(() => {
    if (workspaceSettingsRows.length === 0) return ALL_SIDEBAR_TABS_VISIBLE;
    const ui = mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]);
    return {
      Overview: ui.sidebarShowOverview,
      'Intake Form': ui.sidebarShowIntakeForm,
      Vitals: ui.sidebarShowVitals,
      Allergies: ui.sidebarShowAllergies,
      'Treatment Record': ui.sidebarShowTreatmentRecord,
      Contact: ui.sidebarShowContact,
      Visits: ui.sidebarShowVisits,
      Pharmacy: ui.sidebarShowPharmacy,
      // PRD.262 — Patient Packages sidebar tab, clinic-configurable.
      Packages: ui.sidebarShowPackages,
    };
  }, [workspaceSettingsRows]);

  // PRD.226 — body-level Patient Overview placement. Falls back to the
  // historical "show in body" behaviour until settings have loaded.
  const patientOverviewDisplay = useMemo<'sidebar_only' | 'sidebar_and_body'>(() => {
    if (workspaceSettingsRows.length === 0) return 'sidebar_and_body';
    return mapPatientWorkspaceSettingsToUI(workspaceSettingsRows[0]).patientOverviewDisplay;
  }, [workspaceSettingsRows]);

  // ── Resizable sidebar ──
  const {
    width: sbWidth,
    dragging,
    startDrag,
    resetWidth,
  } = useResizableSidebar({
    storageKey: 'powercareai:patient-sidebar-width',
    min: 280,
    max: 520,
    defaultWidth: 360,
    collapsed,
    onExpand: toggle,
  });

  const brandTokens = useBrandTokens();
  const navigate = useNavigate();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();

  // Routes that need full-width content (no canvas max-width)
  const isFullWidthRoute = patientId
    ? location.pathname === `/patient/${patientId}` ||
      location.pathname.startsWith(`/patient/${patientId}/encounters`)
    : false;

  // Right drawer (collapsed sidebar tool panels)
  const [rightDrawerPanel, setRightDrawerPanel] = useState<DrawerPanel>(null);
  const toggleDrawerPanel = useCallback((panel: DrawerPanel) => {
    setRightDrawerPanel((prev) => (prev === panel ? null : panel));
  }, []);
  // Close drawer when sidebar expands
  useEffect(() => {
    if (!collapsed) setRightDrawerPanel(null);
  }, [collapsed]);

  const [intakeDrawerOpen, setIntakeDrawerOpen] = useState(false);
  const [vitalsDrawerOpen, setVitalsDrawerOpen] = useState(false);
  const [medicationDrawerOpen, setMedicationDrawerOpen] = useState(false);
  const [familyDrawerOpen, setFamilyDrawerOpen] = useState(false);
  const [editPatientDrawerOpen, setEditPatientDrawerOpen] = useState(false);
  // Sidebar timeline drill-in drawers (PRD.180): clicking Forms/Invoices/Files
  // from the sidebar timeline opens a contextual right drawer instead of
  // replacing the main content area.
  const [timelineFormDrawer, setTimelineFormDrawer] = useState<{
    formInstanceId: string;
    encounterId: string;
  } | null>(null);
  const [timelineInvoiceDrawer, setTimelineInvoiceDrawer] = useState<{
    invoiceId: string;
    encounterId: string;
  } | null>(null);
  const [timelineFileDrawer, setTimelineFileDrawer] = useState<{
    fileId: string;
    encounterId: string;
  } | null>(null);
  const [editDrawerMode, setEditDrawerMode] = useState<'details' | 'contacts'>('details');
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);

  // ── Permissions ──
  const orgId = claims?.activeOrg;
  const roles = orgId ? (claims?.orgs?.[orgId]?.roles ?? []) : [];
  const isAdmin = roles.includes('admin');

  const handleCreateEncounter = useCallback(async () => {
    if (!patientId || !orgId || !db) return;
    const { encounterId } = await ensureVisitThenCreateEncounter({ db, orgId, patientId });
    navigate(`/patient/${patientId}/encounters/${encounterId}`);
  }, [db, orgId, patientId, navigate]);

  // ── PowerSync queries ──
  const { data: patientRows = [] } = usePowerSyncQuery<PatientDetailRow>(
    qPatientById(patientId ?? ''),
  );
  const patient = patientRows[0];

  const { data: contactRows = [] } = usePowerSyncQuery<PatientContactRow>(
    qPatientContact(patientId ?? ''),
  );
  const contactRow = contactRows[0];

  const { data: allergyRows = [] } = usePowerSyncQuery<PatientAllergyRow>(
    qPatientAllergies(patientId ?? ''),
  );

  const { data: emergencyContactRows = [] } = usePowerSyncQuery<PatientEmergencyContactRow>(
    qPatientEmergencyContacts(patientId ?? ''),
  );
  const emergencyContact = emergencyContactRows[0];

  const { data: intakeFormRows = [] } = usePowerSyncQuery<PatientIntakeFormRow>(
    qLatestPatientIntakeForm(patientId ?? ''),
  );
  const latestIntakeForm = intakeFormRows[0];

  const intakeResponseSpec = useMemo(
    () => (latestIntakeForm ? qIntakeFormResponse(latestIntakeForm.id) : qNoOp('intake-response')),
    [latestIntakeForm],
  );
  const { data: intakeResponseRows = [] } =
    usePowerSyncQuery<IntakeFormResponseRow>(intakeResponseSpec);
  const intakeResponse = intakeResponseRows[0];

  // ── Published intake form definition ──
  const { data: publishedDefRows = [] } = usePowerSyncQuery<IntakeFormDefinitionRow>(
    qLatestPublishedIntakeFormDefinition(),
  );

  // ── Vitals queries ──
  const { data: vitalDefinitionRows = [] } =
    usePowerSyncQuery<VitalDefinitionRow>(qActiveVitalDefinitions());

  const { data: latestVitalsTimestampRows = [] } = usePowerSyncQuery<{ recorded_at: string }>(
    patientId ? qLatestPatientVitalsRecordedAt(patientId) : qNoOp('vitals-latest-ts'),
  );
  const latestVitalsRecordedAt = latestVitalsTimestampRows[0]?.recorded_at ?? null;

  // Single query — avoids two-step chain delay when new vitals are recorded
  const { data: latestVitalEntryRows = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    patientId ? qLatestPatientVitals(patientId) : qNoOp('vitals-latest-entries'),
  );

  const latestVitalsGrouped = useMemo(() => {
    if (latestVitalEntryRows.length === 0) return null;
    const primary: PatientVitalEntryWithDefinitionRow[] = [];
    const additional: PatientVitalEntryWithDefinitionRow[] = [];
    let mostRecentAt = '';
    for (const row of latestVitalEntryRows) {
      const cat = row.category ?? 'primary';
      if (cat === 'additional') additional.push(row);
      else primary.push(row);
      if (row.recorded_at > mostRecentAt) mostRecentAt = row.recorded_at;
    }
    return { primary, additional, recordedAt: mostRecentAt };
  }, [latestVitalEntryRows]);

  const { data: vitalsHistoryRows = [] } = usePowerSyncQuery<PatientVitalEntryWithDefinitionRow>(
    patientId ? qPatientVitalsHistory(patientId) : qNoOp('vitals-history'),
  );

  // ── Today's encounters (for vitals attach selector) ──
  const { data: todayEncounterRows = [] } = usePowerSyncQuery<PatientEncounterRow>(
    patientId ? qEncountersTodayByPatient(patientId) : qNoOp('encounters-today'),
  );
  const todayEncounterOptions = useMemo(() => {
    return todayEncounterRows.map((e) => {
      const date = e.started_at
        ? new Date(e.started_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        : '';
      const time = e.started_at
        ? new Date(e.started_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
        : '';
      const type = e.type
        ? e.type.charAt(0).toUpperCase() + e.type.slice(1).replace('_', ' ')
        : 'Encounter';
      const provider = e.provider_name ? ` — ${e.provider_name}` : '';
      const status = e.status === 'open' ? ' [Open]' : '';
      return { id: e.id, label: `${type}${provider} · ${date} ${time}${status}` };
    });
  }, [todayEncounterRows]);

  // ── Patient alert notes ──
  const { data: alertNotes = [] } = usePowerSyncQuery<PatientNoteRow>(
    patientId ? qPatientAlertNotes(patientId) : qNoOp('alert-notes'),
  );

  // ── Treatment records (sidebar mini-card) ──
  const { data: recentTreatmentRecords = [] } = usePowerSyncQuery<PatientTreatmentRecordRow>(
    patientId ? qRecentTreatmentRecords(patientId) : qNoOp('treatment-records'),
  );

  // ── Patient billing aggregate (PRD.218) ──
  // Single Dinero-backed source of truth for the sidebar balance chip
  // AND the Patient Overview billing card. `outstandingBalance` is a
  // legacy-shape adapter for chip components — both surfaces ultimately
  // read from `usePatientBilling`, so the numbers stay in lockstep.
  const billing = usePatientBilling(patientId);
  const outstandingBalance: SidebarBilling | null = useMemo(() => {
    if (!patientId) return null;
    if (billing.chargedCents === 0 && billing.paidCents === 0) return null;
    return {
      total_balance: billing.balance.toRoundedUnit(2),
      unpaid_count: billing.unpaidCount,
      has_overdue: billing.hasOverdue ? 1 : 0,
    };
  }, [patientId, billing]);

  // ── Medication form (effective fields + questionnaire) ──
  const { data: dbOverrides = [] } = usePowerSyncQuery<PatientFieldDefinitionRow>(
    qPatientFieldDefinitionOverrides(),
  );
  const effectiveFields = useMemo(
    () => buildPatientFormSettingsVM(PATIENT_CORE_FIELDS, dbOverrides),
    [dbOverrides],
  );
  const medicationQuestionnaire = useMemo(
    () =>
      buildMultiSectionQuestionnaire(
        effectiveFields,
        'Medication',
        MEDICATION_SECTIONS,
        MEDICATION_OPTIONS,
      ),
    [effectiveFields],
  );
  const medicationValidationSchema = useMemo(
    () => buildPatientValidationSchema(effectiveFields.filter((f) => f.step === 'Medication')),
    [effectiveFields],
  );

  // ── Edit drawer questionnaires (Personal Info / Contact + Emergency Contact) ──
  // PRD.232 — ID Type options come from Clinic Settings → regional config.
  // The drawer doesn't seed an existing `patient_identifiers.id_type`, so
  // we don't need to preserve a current value here; the form always opens
  // with idType blank.
  const enabledIdTypeLabels = useEnabledIdTypeLabels();
  const personalInfoOptions = useMemo<FieldOptionsMap>(
    () => ({ ...PERSONAL_INFO_OPTIONS, idType: enabledIdTypeLabels.map(opt) }),
    [enabledIdTypeLabels],
  );

  const detailsQuestionnaire = useMemo(
    () => buildStepQuestionnaire(effectiveFields, 'Personal Info', personalInfoOptions),
    [effectiveFields, personalInfoOptions],
  );
  const detailsValidationSchema = useMemo(
    () => buildPatientValidationSchema(effectiveFields.filter((f) => f.step === 'Personal Info')),
    [effectiveFields],
  );
  const contactsQuestionnaire = useMemo(() => {
    const contactQ = buildStepQuestionnaire(effectiveFields, 'Contact');
    const ecQ = buildStepQuestionnaire(
      effectiveFields,
      'Emergency Contact',
      EMERGENCY_CONTACT_OPTIONS,
    );
    return {
      ...contactQ,
      id: 'patient-contacts-combined',
      sections: [...contactQ.sections, ...ecQ.sections],
    };
  }, [effectiveFields]);
  const contactsValidationSchema = useMemo(
    () =>
      buildPatientValidationSchema(
        effectiveFields.filter((f) => f.step === 'Contact' || f.step === 'Emergency Contact'),
      ),
    [effectiveFields],
  );

  // Country picker config for contacts drawer
  const contactCountryConfig = useMemo(() => {
    const mccField = effectiveFields.find((f) => f.field_key === 'mobileCountryCode');
    return getMobileCountryPickerConfig(mccField?.validation_override);
  }, [effectiveFields]);

  // Show every country in the picker; configured `defaultCountry` is still the pre-selected one.
  const contactCountryOptions = useMemo(() => buildCountryOptions(ALL_COUNTRIES), []);

  // State stores ISO2; converted to/from dial code at load/save boundaries.
  const contactsFieldRenderers = useMemo<Record<string, FieldRenderer>>(
    () => ({
      mobileCountryCode: ({ value, onChange, readOnly }) => {
        const iso2 = typeof value === 'string' ? value : contactCountryConfig.defaultCountry;
        return (
          <CountryPicker
            options={contactCountryOptions}
            valueIso2={iso2}
            onChangeIso2={(newIso2) => onChange('mobileCountryCode', newIso2)}
            disabled={readOnly}
          />
        );
      },
    }),
    [contactCountryOptions, contactCountryConfig.defaultCountry],
  );

  // ── Intake form questionnaire (from DB definition, fallback to fixture) ──
  const fixtureQuestionnaire = publishedQuestionnaire as unknown as IntakeQuestionnaire;
  const { intakeQuestionnaire, intakeBanner, intakeValidationSchema } = useMemo(() => {
    if (publishedDefRows.length === 0) {
      return {
        intakeQuestionnaire: fixtureQuestionnaire,
        intakeBanner: {
          type: 'info' as const,
          message: 'No published intake form found. Publish one in Settings \u2192 Intake Forms.',
        },
        intakeValidationSchema: null,
      };
    }
    const row = publishedDefRows[0];
    let parsed: IntakeQuestionnaire | null = null;
    if (row.schema != null && row.schema !== '') {
      try {
        parsed = JSON.parse(row.schema) as IntakeQuestionnaire;
      } catch {
        // invalid JSON
      }
    }
    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.sections)) {
      return {
        intakeQuestionnaire: fixtureQuestionnaire,
        intakeBanner: {
          type: 'danger' as const,
          message:
            'Published intake form schema is invalid. Please fix in Settings \u2192 Intake Forms.',
        },
        intakeValidationSchema: null,
      };
    }
    return {
      intakeQuestionnaire: parsed,
      intakeBanner: null,
      intakeValidationSchema: row.validation_schema ?? null,
    };
  }, [publishedDefRows]);

  // ── Derived display values ──
  const patientName = patient?.full_name ?? 'Unknown';
  const initials = getInitials(patientName);
  const age = computeAge(patient?.birth_date ?? null);
  const allergyItems = useMemo(() => toAllergyItems(allergyRows), [allergyRows]);
  const allergyNames = useMemo(
    () => allergyItems.map((a) => a.name).filter(Boolean),
    [allergyItems],
  );
  const contactData = useMemo(
    () => toContactData(contactRow, emergencyContact),
    [contactRow, emergencyContact],
  );
  const intakeSummaryBullets = useMemo(
    () => buildIntakeSummaryBullets(intakeResponse?.answers ?? null),
    [intakeResponse],
  );

  // ── Register patient tab in top bar ──
  const { tabs: patientTabs, openPatientTab, closePatientTab } = usePatientTabs();

  // When a patient tab is closed, drop its sidebar state and "applied" flag
  // so reopening that patient seeds from the clinic default again (PRD.181).
  useEffect(() => {
    const openIds = new Set(patientTabs.map((t) => t.id));
    setSidebarStateByPatient((prev) => {
      let changed = false;
      const next: Record<string, boolean> = {};
      for (const [id, v] of Object.entries(prev)) {
        if (openIds.has(id)) next[id] = v;
        else changed = true;
      }
      return changed ? next : prev;
    });
    for (const id of Array.from(sidebarAppliedForRef.current)) {
      if (!openIds.has(id)) sidebarAppliedForRef.current.delete(id);
    }
  }, [patientTabs]);
  useEffect(() => {
    if (!patientId || !patient) return;
    openPatientTab({
      id: patientId,
      label: getShortLabel(patientName),
      fullName: patientName,
      initials: getInitialsShort(patientName),
      color: getPatientColor(patientId),
      photoUrl: patient?.photo_url ?? null,
    });
  }, [patientId, patient, patientName, openPatientTab]);

  // ── Initial intake answers (prefill from latest response) ──
  const initialIntakeAnswers = useMemo<IntakeAnswers>(() => {
    if (!intakeResponse?.answers) return {};
    try {
      return JSON.parse(intakeResponse.answers) as IntakeAnswers;
    } catch {
      return {};
    }
  }, [intakeResponse]);

  // Local state for drawers that edit (start from DB data)
  const initialMedicationAnswers = useMemo<IntakeAnswers>(() => {
    const a: IntakeAnswers = {};
    if (patient) {
      // drugAllergies / foodAllergies are set below based on allergy rows
      if (patient.blood_group_abo) a.aboBloodGroup = patient.blood_group_abo;
      if (patient.blood_group_rh) a.rhAntigen = patient.blood_group_rh;
      if (patient.medical_notes) a.notes = patient.medical_notes;
      if (patient.medical_alerts) a.alerts = patient.medical_alerts;
      if (patient.past_medication_notes) a.pastMedication = patient.past_medication_notes;
      if (patient.current_medication_notes) a.currentMedication = patient.current_medication_notes;
      if (patient.medical_history) a.medicalHistory = patient.medical_history;
    }
    // Prefill from allergy rows
    const drugRow = allergyRows.find((r) => r.allergy_type === 'drug');
    if (drugRow) {
      a.drugAllergies = 'Yes';
      if (drugRow.substance) a.drugAllergiesSpecify = drugRow.substance;
      if (drugRow.reaction) a.drugAllergySymptoms = drugRow.reaction;
      if (drugRow.severity) a.drugAllergySeverity = drugRow.severity;
    } else {
      a.drugAllergies = 'No Known Allergies';
    }
    const foodRow = allergyRows.find((r) => r.allergy_type === 'food');
    if (foodRow) {
      a.foodAllergies = 'Yes';
      if (foodRow.substance) a.foodAllergiesSpecify = foodRow.substance;
      if (foodRow.reaction) a.foodAllergySymptoms = foodRow.reaction;
      if (foodRow.severity) a.foodAllergySeverity = foodRow.severity;
    } else {
      a.foodAllergies = 'No Known Allergies';
    }
    return a;
  }, [patient, allergyRows]);

  // ── Initial answers for Edit Details drawer ──
  const initialDetailsAnswers = useMemo<IntakeAnswers>(() => {
    const a: IntakeAnswers = {};
    if (patient) {
      if (patient.patient_number) a.patientId = patient.patient_number;
      if (patient.full_name) a.patientName = patient.full_name;
      if (patient.title) a.title = patient.title;
      if (patient.assigned_doctor) a.doctor = patient.assigned_doctor;
      if (patient.sex) a.gender = patient.sex;
      if (patient.birth_date) a.dateOfBirth = patient.birth_date;
      if (patient.marital_status) a.maritalStatus = patient.marital_status;
      if (patient.local_name) a.localName = patient.local_name;
      if (patient.country_of_birth) a.countryOfBirth = patient.country_of_birth;
      if (patient.nationality) a.nationality = patient.nationality;
      if (patient.race) a.race = patient.race;
      if (patient.religion) a.religion = patient.religion;
      if (patient.occupation) a.occupation = patient.occupation;
    }
    return a;
  }, [patient]);

  // ── Initial answers for Edit Contacts drawer ──
  const initialContactsAnswers = useMemo<IntakeAnswers>(() => {
    const a: IntakeAnswers = {};
    if (contactRow) {
      if (contactRow.email) a.email = contactRow.email;
      a.mobileCountryCode = dialCodeToIso2(
        contactRow.mobile_country_code,
        contactCountryOptions,
        contactCountryConfig.defaultCountry,
      );
      if (contactRow.mobile_number) a.mobileNumber = contactRow.mobile_number;
      if (contactRow.formal_telephone) a.telephone = contactRow.formal_telephone;
      if (contactRow.formal_postal_code) a.postalCode = contactRow.formal_postal_code;
      if (contactRow.formal_residential_address)
        a.residentialAddress = contactRow.formal_residential_address;
      if (contactRow.formal_province_state) a.provinceState = contactRow.formal_province_state;
      if (contactRow.formal_district) a.district = contactRow.formal_district;
    }
    if (emergencyContact) {
      if (emergencyContact.name) a.emergencyContactName = emergencyContact.name;
      if (emergencyContact.relationship)
        a.emergencyContactRelationship = emergencyContact.relationship;
      if (emergencyContact.phone) a.emergencyContactPhone = emergencyContact.phone;
      if (emergencyContact.email) a.emergencyContactEmail = emergencyContact.email;
    }
    return a;
  }, [contactRow, emergencyContact, contactCountryOptions, contactCountryConfig.defaultCountry]);

  // ── Save: patient details / contacts (IntakeAnswers) ──
  const handleDetailsSave = useCallback(
    async (answers: IntakeAnswers) => {
      if (!patientId || !db) return;
      const now = new Date().toISOString();
      const s = (k: string) =>
        typeof answers[k] === 'string' && answers[k] ? (answers[k] as string) : null;

      // UPDATE patients — personal info columns
      await db.execute(
        `UPDATE patients SET full_name = ?, title = ?, assigned_doctor = ?,
         sex = ?, birth_date = ?, marital_status = ?,
         local_name = ?, country_of_birth = ?, nationality = ?,
         race = ?, religion = ?, occupation = ?, updated_at = ?
         WHERE id = ?`,
        [
          s('patientName'),
          s('title'),
          s('doctor'),
          s('gender'),
          s('dateOfBirth'),
          s('maritalStatus'),
          s('localName'),
          s('countryOfBirth'),
          s('nationality'),
          s('race'),
          s('religion'),
          s('occupation'),
          now,
          patientId,
        ],
      );
    },
    [patientId, db],
  );

  const handleContactsSave = useCallback(
    async (answers: IntakeAnswers) => {
      if (!patientId || !db) return;
      const s = (k: string) =>
        typeof answers[k] === 'string' && answers[k] ? (answers[k] as string) : null;

      // Convert ISO2 → dial code for DB storage
      const mccIso2 = s('mobileCountryCode');
      const mccDialCode = mccIso2 ? iso2ToDialCode(mccIso2) : null;

      // UPSERT patient_contacts
      if (contactRow) {
        await db.execute(
          `UPDATE patient_contacts SET email = ?, mobile_country_code = ?, mobile_number = ?,
           formal_telephone = ?, formal_postal_code = ?,
           formal_residential_address = ?, formal_province_state = ?,
           formal_district = ?
           WHERE id = ?`,
          [
            s('email'),
            mccDialCode,
            s('mobileNumber'),
            s('telephone'),
            s('postalCode'),
            s('residentialAddress'),
            s('provinceState'),
            s('district'),
            contactRow.id,
          ],
        );
      } else {
        const contactId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO patient_contacts (id, patient_id, email, mobile_country_code, mobile_number,
           formal_telephone, formal_postal_code,
           formal_residential_address, formal_province_state, formal_district)
           VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
          [
            contactId,
            patientId,
            s('email'),
            mccDialCode,
            s('mobileNumber'),
            s('telephone'),
            s('postalCode'),
            s('residentialAddress'),
            s('provinceState'),
            s('district'),
          ],
        );
      }

      // UPSERT patient_emergency_contacts
      const ecName = s('emergencyContactName');
      const ecPhone = s('emergencyContactPhone');
      if (emergencyContact) {
        await db.execute(
          `UPDATE patient_emergency_contacts SET name = ?, relationship = ?, phone = ?, email = ?
           WHERE id = ?`,
          [
            ecName,
            s('emergencyContactRelationship'),
            ecPhone,
            s('emergencyContactEmail'),
            emergencyContact.id,
          ],
        );
      } else if (ecName || ecPhone) {
        const ecId = crypto.randomUUID();
        await db.execute(
          `INSERT INTO patient_emergency_contacts (id, patient_id, name, relationship, phone, email)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [
            ecId,
            patientId,
            ecName,
            s('emergencyContactRelationship'),
            ecPhone,
            s('emergencyContactEmail'),
          ],
        );
      }
    },
    [patientId, db, contactRow, emergencyContact],
  );

  // ── Save: medication (patients columns + replace-all allergies) ──
  const handleMedicationSave = useCallback(
    async (answers: IntakeAnswers) => {
      if (!patientId || !db) return;
      const now = new Date().toISOString();
      const s = (k: string) =>
        typeof answers[k] === 'string' && answers[k] ? (answers[k] as string) : null;

      // UPDATE patients — medication columns
      await db.execute(
        `UPDATE patients SET blood_group_abo = ?, blood_group_rh = ?,
         drug_allergies_notes = ?, food_allergies_notes = ?,
         medical_notes = ?, medical_alerts = ?,
         past_medication_notes = ?, current_medication_notes = ?,
         medical_history = ?, updated_at = ? WHERE id = ?`,
        [
          s('aboBloodGroup'),
          s('rhAntigen'),
          s('drugAllergies'),
          s('foodAllergies'),
          s('notes'),
          s('alerts'),
          s('pastMedication'),
          s('currentMedication'),
          s('medicalHistory'),
          now,
          patientId,
        ],
      );

      // Replace-all patient_allergies
      await db.execute(
        'UPDATE patient_allergies SET deleted_at = ? WHERE patient_id = ? AND deleted_at IS NULL',
        [now, patientId],
      );

      // Insert drug allergy row (if not "No Known Allergies" / empty)
      const drugSel = s('drugAllergies');
      if (drugSel && drugSel !== 'No Known Allergies') {
        const substance = drugSel === 'Yes' ? s('drugAllergiesSpecify') || 'Unspecified' : drugSel;
        await db.execute(
          `INSERT INTO patient_allergies (id, patient_id, allergy_type, substance, reaction, severity)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            patientId,
            'drug',
            substance,
            s('drugAllergySymptoms'),
            s('drugAllergySeverity'),
          ],
        );
      }

      // Insert food allergy row (if not "No Known Allergies" / empty)
      const foodSel = s('foodAllergies');
      if (foodSel && foodSel !== 'No Known Allergies') {
        const substance = foodSel === 'Yes' ? s('foodAllergiesSpecify') || 'Unspecified' : foodSel;
        await db.execute(
          `INSERT INTO patient_allergies (id, patient_id, allergy_type, substance, reaction, severity)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [
            crypto.randomUUID(),
            patientId,
            'food',
            substance,
            s('foodAllergySymptoms'),
            s('foodAllergySeverity'),
          ],
        );
      }
    },
    [patientId, db],
  );

  // ── Save: intake form (new session + new response) ──
  const handleIntakeSave = useCallback(
    async (answers: IntakeAnswers) => {
      if (!patientId || !db) return;
      const publishedDef = publishedDefRows[0];
      if (!publishedDef) return;
      const now = new Date().toISOString();

      // INSERT new patient_intake_forms session
      const patientIntakeFormId = crypto.randomUUID();
      await db.execute(
        `INSERT INTO patient_intake_forms (id, patient_id, intake_form_definition_id, status, completed_at)
         VALUES (?, ?, ?, ?, ?)`,
        [patientIntakeFormId, patientId, publishedDef.id, 'completed', now],
      );

      // INSERT new intake_form_responses
      const responseId = crypto.randomUUID();
      await db.execute(
        `INSERT INTO intake_form_responses (id, patient_intake_form_id, answers)
         VALUES (?, ?, ?)`,
        [responseId, patientIntakeFormId, JSON.stringify(answers)],
      );
    },
    [patientId, db, publishedDefRows],
  );

  // ── Delete patient (soft delete) ──
  const handleDeletePatient = useCallback(async () => {
    if (!patientId || !db) return;
    await db.execute('UPDATE patients SET status = ?, updated_at = ? WHERE id = ?', [
      'archived',
      new Date().toISOString(),
      patientId,
    ]);
    closePatientTab(patientId);
    setDeleteConfirmOpen(false);
    navigate('/patients');
  }, [patientId, db, closePatientTab, navigate]);

  const handlePhotoSelected = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file || !db || !patientId || !orgId) return;
      // Reset input so the same file can be re-selected
      e.target.value = '';
      try {
        const result = await uploadToStorage({
          orgId,
          patientId,
          file,
          fileId: 'avatar',
        });
        await db.execute('UPDATE patients SET photo_url = ?, updated_at = ? WHERE id = ?', [
          result.downloadUrl,
          new Date().toISOString(),
          patientId,
        ]);
      } catch (err) {
        console.error('[Upload Photo] failed:', err);
      }
    },
    [db, patientId, orgId],
  );

  const outletContext: PatientOutletContext = useMemo(
    () => ({
      collapsed,
      onToggleSidebar: toggle,
      patient: patient ?? null,
      patientName,
      age,
      sex: patient?.sex ?? null,
      contactData,
      allergyItems,
      latestVitals: latestVitalsGrouped,
      alertNotes,
      recentTreatmentRecords,
      outstandingBalance,
      patientOverviewDisplay,
    }),
    [
      collapsed,
      toggle,
      patient,
      patientName,
      age,
      contactData,
      allergyItems,
      latestVitalsGrouped,
      alertNotes,
      recentTreatmentRecords,
      outstandingBalance,
      patientOverviewDisplay,
    ],
  );

  return (
    <div
      className="pc-desk"
      style={
        {
          ...brandTokens,
          '--sidebar-width-expanded': 'var(--patient-sidebar-width)',
          display: 'grid',
          gridTemplateColumns: '1fr',
          gridTemplateRows: 'var(--topbar-height) 1fr',
          height: '100vh',
          fontFamily: 'var(--font-family)',
        } as CSSProperties
      }
    >
      <TopBar sidebarWidth={collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth} />

      {/* Main content area — matches clinic mode */}
      <div
        style={{
          padding: 'var(--content-inset)',
          position: 'relative',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Main Card */}
        <div
          style={{
            background: 'transparent',
            borderRadius: 'var(--main-card-radius)',
            boxShadow: 'var(--shadow-paper)',
            height: '100%',
            display: 'flex',
            overflow: 'clip',
          }}
        >
          {/* Sidebar */}
          <div
            style={{
              width: collapsed ? SIDEBAR_COLLAPSED_WIDTH : sbWidth,
              flexShrink: 0,
              transition: dragging ? 'none' : 'width 200ms ease-out',
              overflow: 'hidden',
            }}
          >
            <PatientSidebar
              collapsed={collapsed}
              onExpand={toggle}
              activeDrawer={rightDrawerPanel}
              onToggleDrawer={toggleDrawerPanel}
              patientName={patientName}
              initials={initials}
              avatarColor={getPatientColor(patientId ?? '')}
              age={age}
              sex={patient?.sex ?? null}
              status={patient?.status ?? null}
              allergyNames={allergyNames}
              allergyItems={allergyItems}
              contactData={contactData}
              intakeStatus={latestIntakeForm?.status ?? null}
              intakeCompletedAt={latestIntakeForm?.completed_at ?? null}
              intakeSummaryBullets={intakeSummaryBullets}
              onEditIntake={() => setIntakeDrawerOpen(true)}
              onAddVitals={() => setVitalsDrawerOpen(true)}
              onUpdateMedication={() => setMedicationDrawerOpen(true)}
              onEditFamily={() => setFamilyDrawerOpen(true)}
              onEditContact={() => {
                setEditDrawerMode('contacts');
                setEditPatientDrawerOpen(true);
              }}
              onEditPersonalInfo={() => {
                setEditDrawerMode('details');
                setEditPatientDrawerOpen(true);
              }}
              onUpdatePhoto={() => photoInputRef.current?.click()}
              onDeletePatient={() => setDeleteConfirmOpen(true)}
              isAdmin={isAdmin}
              photoUrl={patient?.photo_url ?? null}
              latestVitals={latestVitalsGrouped}
              vitalsHistoryRows={vitalsHistoryRows}
              intakeQuestionnaire={intakeQuestionnaire}
              intakeAnswers={initialIntakeAnswers}
              medicationInfo={{
                bloodGroupAbo: patient?.blood_group_abo ?? '',
                bloodGroupRh: patient?.blood_group_rh ?? '',
                pastMedication: patient?.past_medication_notes ?? '',
                currentMedication: patient?.current_medication_notes ?? '',
                medicalHistory: patient?.medical_history ?? '',
                notes: patient?.medical_notes ?? '',
                alerts: patient?.medical_alerts ?? '',
              }}
              alertNotes={alertNotes}
              recentTreatmentRecords={recentTreatmentRecords}
              onViewTreatmentRecords={() =>
                navigate(`/patient/${patientId}/encounters?view=treatment`)
              }
              patientId={patientId ?? ''}
              userId={user?.uid ?? null}
              activeEncounterId={activeEncounterId}
              onSelectEncounter={(encId) => navigate(`/patient/${patientId}/encounters/${encId}`)}
              onCreateEncounter={handleCreateEncounter}
              onOpenFormDrilldown={(formInstanceId, encounterId) =>
                setTimelineFormDrawer({ formInstanceId, encounterId })
              }
              onOpenInvoiceDrilldown={(invoiceId, encounterId) =>
                setTimelineInvoiceDrawer({ invoiceId, encounterId })
              }
              onOpenFileDrilldown={(fileId, encounterId) =>
                setTimelineFileDrawer({ fileId, encounterId })
              }
              outstandingBalance={outstandingBalance}
              sidebarTabVisibility={sidebarTabVisibility}
            />
          </div>

          <ResizeGutter onDragStart={startDrag} onDoubleClick={resetWidth} />

          {/* Content Area */}
          <div
            style={{
              flex: 1,
              minWidth: 0,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
              overflow: 'hidden',
              background: '#ffffff',
              borderRadius: '0 var(--main-card-radius) var(--main-card-radius) 0',
            }}
          >
            {/* Scrollable Content Canvas */}
            {isFullWidthRoute ? (
              <div
                style={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  flexDirection: 'column',
                  overflow: 'hidden',
                  paddingTop: 'var(--sp-1)',
                }}
              >
                <Outlet context={outletContext} />
              </div>
            ) : (
              <div
                style={{
                  flex: 1,
                  overflowY: 'auto',
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <div
                  className={`patient-canvas${collapsed ? ' patient-canvas--collapsed' : ''}`}
                  style={{
                    width: '100%',
                    padding: 'var(--sp-6)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-6)',
                  }}
                >
                  <Outlet context={outletContext} />
                </div>
              </div>
            )}

            {/* Right Drawer — opened from collapsed sidebar tool icons */}
            <RightDrawer
              open={rightDrawerPanel != null}
              onClose={() => setRightDrawerPanel(null)}
              title={rightDrawerPanel ?? ''}
              icon={
                rightDrawerPanel === 'Allergies'
                  ? 'alert-triangle'
                  : (RAIL_TOOLS.find((t) => t.label === rightDrawerPanel)?.icon ?? 'layout-grid')
              }
            >
              {rightDrawerPanel === 'Overview' && (
                <>
                  <IntakeFormCard
                    status={latestIntakeForm?.status ?? null}
                    completedAt={latestIntakeForm?.completed_at ?? null}
                    summaryBullets={intakeSummaryBullets}
                    onEdit={() => setIntakeDrawerOpen(true)}
                  />
                  <div style={{ height: 'var(--sp-4)' }} />
                  <VitalsCard
                    latestVitals={latestVitalsGrouped}
                    onAdd={() => setVitalsDrawerOpen(true)}
                  />
                  <div style={{ height: 'var(--sp-4)' }} />
                  <AllergiesCard
                    allergyItems={allergyItems}
                    onEdit={() => setMedicationDrawerOpen(true)}
                  />
                  <div style={{ height: 'var(--sp-4)' }} />
                  <TreatmentRecordCard
                    records={recentTreatmentRecords}
                    onAdd={() => navigate(`/patient/${patientId}/encounters?view=treatment`)}
                    onViewAll={() => navigate(`/patient/${patientId}/encounters?view=treatment`)}
                  />
                </>
              )}
              {rightDrawerPanel === 'Vitals' && (
                <VitalsDetailView
                  onAddVitals={() => setVitalsDrawerOpen(true)}
                  latestVitals={latestVitalsGrouped}
                  historyRows={vitalsHistoryRows}
                />
              )}
              {rightDrawerPanel === 'Visits' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--sp-3)',
                  }}
                >
                  <PatientVisitNoteCTA
                    patientId={patientId ?? ''}
                    onContinue={(encId) => navigate(`/patient/${patientId}/encounters/${encId}`)}
                    onCreate={handleCreateEncounter}
                  />
                  <SidebarVisitsPanel
                    patientId={patientId ?? ''}
                    selectedEncounterId={activeEncounterId}
                    onSelectEncounter={(encId) =>
                      navigate(`/patient/${patientId}/encounters/${encId}`)
                    }
                  />
                </div>
              )}
              {rightDrawerPanel === 'Treatment Record' && (
                <TreatmentRecordSidebarView
                  patientId={patientId ?? ''}
                  onAdd={() => navigate(`/patient/${patientId}/encounters?view=treatment`)}
                  onViewAll={() => navigate(`/patient/${patientId}/encounters?view=treatment`)}
                />
              )}
              {rightDrawerPanel === 'Allergies' && (
                <AllergiesDetailView
                  onUpdateMedication={() => setMedicationDrawerOpen(true)}
                  allergies={allergyItems}
                  medicationInfo={{
                    bloodGroupAbo: patient?.blood_group_abo ?? '',
                    bloodGroupRh: patient?.blood_group_rh ?? '',
                    pastMedication: patient?.past_medication_notes ?? '',
                    currentMedication: patient?.current_medication_notes ?? '',
                    medicalHistory: patient?.medical_history ?? '',
                    notes: patient?.medical_notes ?? '',
                    alerts: patient?.medical_alerts ?? '',
                  }}
                />
              )}
              {rightDrawerPanel === 'Pharmacy' && (
                <SidebarPharmacyPanel
                  patientId={patientId ?? ''}
                  onViewAll={() => navigate(`/patient/${patientId}/pharmacy`)}
                />
              )}
              {rightDrawerPanel === 'Packages' && (
                <SidebarPackagesPanel
                  patientId={patientId ?? ''}
                  userId={user?.uid ?? null}
                  isAdmin={isAdmin}
                  activeEncounterId={activeEncounterId ?? null}
                  onViewAll={() => navigate(`/patient/${patientId}/packages`)}
                />
              )}
            </RightDrawer>
          </div>
        </div>
      </div>

      {/* Intake Form Drawer */}
      <UpdateIntakeFormDrawer
        open={intakeDrawerOpen}
        onOpenChange={setIntakeDrawerOpen}
        brandTokens={brandTokens}
        questionnaire={intakeQuestionnaire}
        initialAnswers={initialIntakeAnswers}
        validationSchema={intakeValidationSchema}
        banner={intakeBanner}
        onSave={handleIntakeSave}
      />

      {/* Record Vitals Drawer */}
      <RecordVitalsDrawer
        open={vitalsDrawerOpen}
        onOpenChange={setVitalsDrawerOpen}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        vitalDefinitions={vitalDefinitionRows}
        latestRecordedAt={latestVitalsRecordedAt}
        db={db}
        mode="patient"
        todayEncounters={todayEncounterOptions}
      />

      {/* Update Medication Drawer */}
      <UpdateMedicationDrawer
        open={medicationDrawerOpen}
        onOpenChange={setMedicationDrawerOpen}
        brandTokens={brandTokens}
        initialAnswers={initialMedicationAnswers}
        questionnaire={medicationQuestionnaire}
        validationSchema={medicationValidationSchema}
        onSave={handleMedicationSave}
      />

      {/* PRD.267 — Edit Family History Drawer */}
      <EditFamilyHistoryDrawer
        open={familyDrawerOpen}
        onOpenChange={setFamilyDrawerOpen}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
      />

      {/* Sidebar timeline drill-in drawers (PRD.180) */}
      <TimelineFormDrawer
        open={timelineFormDrawer != null}
        onOpenChange={(o) => {
          if (!o) setTimelineFormDrawer(null);
        }}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        encounterId={timelineFormDrawer?.encounterId ?? null}
        formInstanceId={timelineFormDrawer?.formInstanceId ?? null}
      />
      <TimelineInvoiceDrawer
        open={timelineInvoiceDrawer != null}
        onOpenChange={(o) => {
          if (!o) setTimelineInvoiceDrawer(null);
        }}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        encounterId={timelineInvoiceDrawer?.encounterId ?? null}
        invoiceId={timelineInvoiceDrawer?.invoiceId ?? null}
      />
      <TimelineFileDrawer
        open={timelineFileDrawer != null}
        onOpenChange={(o) => {
          if (!o) setTimelineFileDrawer(null);
        }}
        brandTokens={brandTokens}
        patientId={patientId ?? ''}
        encounterId={timelineFileDrawer?.encounterId ?? null}
        fileId={timelineFileDrawer?.fileId ?? null}
      />

      {/* Edit Patient Details Drawer */}
      <EditPatientDetailsDrawer
        open={editPatientDrawerOpen}
        onOpenChange={setEditPatientDrawerOpen}
        brandTokens={brandTokens}
        mode={editDrawerMode}
        questionnaire={editDrawerMode === 'contacts' ? contactsQuestionnaire : detailsQuestionnaire}
        initialAnswers={
          editDrawerMode === 'contacts' ? initialContactsAnswers : initialDetailsAnswers
        }
        validationSchema={
          editDrawerMode === 'contacts' ? contactsValidationSchema : detailsValidationSchema
        }
        onSave={editDrawerMode === 'contacts' ? handleContactsSave : handleDetailsSave}
        fieldRenderers={editDrawerMode === 'contacts' ? contactsFieldRenderers : undefined}
        patientId={patientId}
      />

      {/* Delete Patient Confirmation Modal */}
      <Modal
        open={deleteConfirmOpen}
        onOpenChange={setDeleteConfirmOpen}
        title="Delete patient?"
        description={`Are you sure you want to delete ${patientName}? This will archive the patient record.`}
        maxWidth={420}
        portalStyle={brandTokens as CSSProperties}
        footer={
          <div style={{ display: 'flex', gap: 'var(--sp-3)', justifyContent: 'flex-end' }}>
            <Button variant="outline" size="md" onClick={() => setDeleteConfirmOpen(false)}>
              Cancel
            </Button>
            <button
              onClick={handleDeletePatient}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: 36,
                padding: '0 var(--sp-4)',
                borderRadius: 'var(--radius-md)',
                border: 'none',
                background: 'var(--color-danger)',
                color: '#fff',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 'var(--font-weight-semi)',
                cursor: 'pointer',
                fontFamily: 'var(--font-family)',
              }}
            >
              Delete Patient
            </button>
          </div>
        }
      >
        <div style={{ padding: 'var(--sp-4) var(--sp-6)' }}>
          <p
            style={{
              margin: 0,
              fontSize: 'var(--font-size-sm)',
              lineHeight: 'var(--line-height-sm)',
              color: 'var(--text-muted)',
            }}
          >
            This action will archive the patient. They will no longer appear in the active patients
            list but can be found under the &ldquo;Archived&rdquo; filter.
          </p>
        </div>
      </Modal>

      {/* Hidden file input for photo upload */}
      <input
        ref={photoInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handlePhotoSelected}
      />
    </div>
  );
}
